import { DebugTransferControls } from "../components/DebugTransferControls";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { RefundBusyOverlay } from "./RefundBusyOverlay";

import {
  api,
  ApiError,
  type CapacityCalculationResponse,
  type CapacityPurchaseInvoiceSyncResponse,
  type CapacityItem,
  type CapacityWorkspace,
  type DataImportBatch,
  type DirectLoadedVatCandidate,
  type InvoiceSyncJob,
  type RefundCase,
  type RefundWorkJob,
  type SourceReconciliation
} from "../api";
import { enqueueUpload, subscribeUploads, type UploadKind, type UploadProgress } from "./uploadQueue";
import { calculationAmount, formatAccountCode, formatMoney as money, formatMoneyText } from "../formatting";
import { MoneyInput } from "../components/MoneyInput";
import { GuidedQuestions, type WorkflowQuestion } from "./GuidedQuestion";
import type { RecipeUpdate } from "./DecisionMatchingPanel";
import { MissingRecipeEditor } from "./MissingRecipeEditor";
import { LoadingSourceReview } from "./LoadingSourceReview";
import { getLoadingAction, hasActionableStockInformation, loadingReviewSummary, pendingPurchaseAnswers, provisionalCalculation, missingLoadingRecipes, unresolvedReturnInvoiceIds } from "./loadingActions";
import { LoadingReview439, type LoadingReviewState } from "./LoadingReview439";

type Props = {
  refundCase: RefundCase;
  canPrepare: boolean;
  canApprove: boolean;
  onBack: () => void;
  onOpenSources?: () => void;
  onReviewInvoices: (request: {
    refundCaseId: number;
    invoiceIds: number[];
    code: string;
    title: string;
    instruction: string;
  }) => void;
};

type QueuedReview = { item: DirectLoadedVatCandidate; explanation: string };
type Workflow439 = {
  round: number; limit: number; questions: WorkflowQuestion[]; needs_choice: boolean; remaining_count: number; decision_count?: number;
  limited_scope: boolean;
  material_review?: { requested?: boolean; required: boolean; trade_count: number; excluded_count: number };
  loading_review?: LoadingReviewState;
  sector_options?: { value: string; label: string }[];
  saved_sector?: string;
  calculation_required?: boolean;
  recipe_learning?: { bucket: string; subject: string; label: string; source: string; year: string; applied: boolean }[];
  production_requirement: { ready: boolean; mode: string; message: string };
  active_job?: RefundWorkJob | null;
  latest_job?: RefundWorkJob | null;
};
type RecipeComponentDraft = {
  custom_material?: boolean;
  draft_key: string;
  component_key: string;
  material_code: string;
  material_name: string;
  material_unit: string;
  material_quantity: string;
};

type MaterialMappings = {
  materials: { id: number; code: string; name: string; base_unit: string }[];
  candidates: { id: number; code: string; name: string; base_unit: string; source_units?: string[] }[];
};
type SmartMaterialResult = {
  material_id: number; confidence: number | null; reason: string; sources: string[];
  applied: boolean; ambiguous: boolean; candidate_count: number;
  candidate: { id: number; code: string; name: string } | null;
};

type DroppedEntry = {
  isFile: boolean;
  isDirectory: boolean;
  file?: (success: (file: File) => void, failure?: (error: DOMException) => void) => void;
  createReader?: () => {
    readEntries: (
      success: (entries: DroppedEntry[]) => void,
      failure?: (error: DOMException) => void
    ) => void;
  };
};

async function filesFromEntry(entry: DroppedEntry): Promise<File[]> {
  if (entry.isFile && entry.file) {
    return new Promise((resolve, reject) => entry.file?.((file) => resolve([file]), reject));
  }
  if (!entry.isDirectory || !entry.createReader) return [];
  const reader = entry.createReader();
  const entries: DroppedEntry[] = [];
  while (true) {
    const next = await new Promise<DroppedEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (!next.length) break;
    entries.push(...next);
  }
  return (await Promise.all(entries.map(filesFromEntry))).flat();
}

async function droppedFiles(dataTransfer: DataTransfer): Promise<File[]> {
  const entries = Array.from(dataTransfer.items)
    .map((item) => (
      item as unknown as { webkitGetAsEntry?: () => DroppedEntry | null }
    ).webkitGetAsEntry?.())
    .filter((entry): entry is DroppedEntry => Boolean(entry));
  if (entries.length) return (await Promise.all(entries.map(filesFromEntry))).flat();
  return Array.from(dataTransfer.files);
}

function withExtensions(files: File[], extensions: string[]): File[] {
  return files.filter((file) => extensions.some((extension) => file.name.toLocaleLowerCase("tr-TR").endsWith(extension)));
}

export async function isLedgerXml(file: File): Promise<boolean> {
  const header = (await file.slice(0, 65536).text()).replace(/<!--[\s\S]*?-->/g, "");
  const root = header.match(/<(?![!?])(?:[\w.-]+:)?([\w.-]+)(?=[\s/>])/);
  if (!root || ["Invoice", "CreditNote", "DespatchAdvice", "ApplicationResponse", "berat"].includes(root[1])) return false;
  return /<(?:[\w.-]+:)?(?:accountingEntries|entryHeader|entryDetail)(?=[\s/>])/.test(header)
    || (root[1] === "defter" && /xmlns(?::[\w.-]+)?\s*=\s*["']https?:\/\/(?:www\.)?edefter\.gov\.tr["']/.test(header));
}


function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function quantity(item: CapacityItem): string {
  return `${Number(item.annual_quantity).toLocaleString("tr-TR")} ${item.unit}`;
}

function capacityRole(item: CapacityItem): string {
  const labels: Record<string, string> = {
    direct_material: "doğrudan hammadde",
    internal_intermediate: "iç üretim",
    production_overhead: "üretim gideri",
    customer_owned: "müşteriye ait"
  };
  return labels[item.economic_role || ""] || "sınıflandırılmadı";
}

export function loadingReviewNeedsPreparation(review?: LoadingReviewState) {
  // Unresolved checks are user actions, not a running preparation job.
  return !review?.prepared || Boolean(review.stale || review.rows.some(row => row.stale));
}

export function invoiceSourcePresentation(
  transactionCode: string,
  summary: CapacityWorkspace["summary"] | undefined,
  processing: { declaration?: boolean; ledger?: boolean } = {},
) {
  if (!summary) return { status: "Bekleniyor", message: "Fatura bilgileri alınıyor." };
  if (!(summary.incoming_invoice_count + summary.outgoing_invoice_count)) {
    return {
      status: summary.taxpayer_invoice_count ? "Dönem dışı" : "Bekleniyor",
      message: "Dosya dönemine ait faturaları Faturalar menüsünden entegratörle çekin veya dosyadan yükleyin.",
    };
  }
  if (transactionCode === "439" && !summary.purchase_invoice_source_complete) {
    const waiting = [];
    if (processing.declaration) waiting.push("beyannamelerin işlenmesi");
    else if (!summary.declarations_ready) waiting.push("beyannamelerin yüklenmesi");
    if (processing.ledger) waiting.push("e-Defter / muavin dosyalarının işlenmesi");
    else if (!summary.ledger_ready) waiting.push("e-Defter / muavin yüklenmesi");
    if (waiting.length) return {
      status: "Bekleniyor",
      message: `Faturaların kontrolü için ${waiting.join(" ve ")} bekleniyor.`,
    };
    const checks = [];
    if (summary.purchase_invoice_integrator_gap_periods.length || summary.purchase_invoice_manual_source_required) {
      checks.push("Alış faturalarının dönem kapsamının doğrulanması bekleniyor. Faturalar menüsünden aktarım kapsamını kontrol edin.");
    }
    if (summary.purchase_invoice_reconciliation_gap_periods.length) {
      checks.push("Beyanname ile alış faturası KDV tutarları arasındaki farkın kontrolü bekleniyor.");
    }
    return { status: "Doğrulama bekleniyor", message: checks.join(" ") || "Alış faturası kaynaklarının doğrulanması bekleniyor." };
  }
  return { status: "Hazır", message: "" };
}

function UploadCard({
  id,
  number,
  title,
  detail,
  status,
  accept,
  multiple = false,
  allowFolder = false,
  disabled = false,
  progress,
  footer,
  children,
  selection,
  onFiles
}: {
  id?: string;
  number: string;
  title: string;
  detail: string;
  status: string;
  accept?: string;
  multiple?: boolean;
  allowFolder?: boolean;
  disabled?: boolean;
  progress?: UploadProgress;
  footer?: string;
  children?: ReactNode;
  selection?: ReactNode;
  onFiles?: (files: File[]) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const displayedStatus = progress ? "İşleniyor" : status;
  const selectFiles = (files: File[]) => {
    if (!disabled && !progress && files.length && onFiles) onFiles(files);
  };
  return (
    <article
      id={id}
      className={`smart-upload-card ${displayedStatus === "Hazır" ? "ready" : ""} ${dragging ? "dragging" : ""}`}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled && !progress && onFiles) setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (!disabled && !progress && onFiles) void droppedFiles(event.dataTransfer).then(selectFiles);
      }}
    >
      <span className="smart-upload-number">{number}</span>
      <div>
        <strong>{title}</strong>
        <small>{detail}</small>
      </div>
      <em>{displayedStatus}</em>
      {footer && <b>{footer}</b>}
      {children}
      {progress ? <div aria-live="polite" className="smart-upload-processing">
        {progress.refreshing ? "Dönemler ve kontroller güncelleniyor…" : progress.current > 0
          ? `${progress.current}/${progress.total}${progress.unit ? ` ${progress.unit}` : ""} işleniyor…`
          : `${progress.total}${progress.unit ? ` ${progress.unit}` : " dosya"} sırada…`}
      </div> : onFiles ? <div className="smart-upload-actions">
        <label aria-disabled={disabled} className="smart-file-button">
          Dosya seç
          <input
            accept={accept}
            disabled={disabled}
            multiple={multiple}
            type="file"
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              event.target.value = "";
              selectFiles(files);
            }}
          />
        </label>
        {allowFolder && <label aria-disabled={disabled} className="smart-file-button secondary">
          Klasör seç
          <input
            ref={(input) => {
              input?.setAttribute("webkitdirectory", "");
              input?.setAttribute("directory", "");
            }}
            disabled={disabled}
            multiple
            type="file"
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              event.target.value = "";
              selectFiles(files);
            }}
          />
        </label>}
        <small>{allowFolder ? "veya klasörü buraya bırakın" : "veya dosyayı buraya bırakın"}</small>
      </div> : null}
      {selection}
    </article>
  );
}

export function SmartRefundWorkspace({ refundCase, canPrepare, canApprove, onBack, onReviewInvoices, onOpenSources }: Props) {
  const [workspace, setWorkspace] = useState<CapacityWorkspace | null>(null);
  const [sourceReconciliation, setSourceReconciliation] = useState<SourceReconciliation | null>(null);
  const [busy, setBusy] = useState("");
  const [selectedOpeningRecords, setSelectedOpeningRecords] = useState<string[]>([]);
  const [focusQuestion, setFocusQuestion] = useState<{ id: string }>();
  const [uploadProgress, setUploadProgress] = useState<Partial<Record<UploadKind, UploadProgress>>>({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [missingBomUnits, setMissingBomUnits] = useState<{ file: File; items: { key: string; name: string; unit: string }[] } | null>(null);
  useEffect(() => { setMissingBomUnits(null); }, [refundCase.id]);
  const [calculationError, setCalculationError] = useState("");
  const [reviewingLineId, setReviewingLineId] = useState<number | null>(null);
  const [savingReviewLineIds, setSavingReviewLineIds] = useState<number[]>([]);
  const [pendingReviewLineIds, setPendingReviewLineIds] = useState<number[]>([]);
  const reviewQueueRef = useRef<QueuedReview[]>([]);
  const reviewQueueRunningRef = useRef(false);
  const loadInFlightRef = useRef<Promise<void> | null>(null);
  const priorLedgerRefresh = useRef(false);
  const [workflow439, setWorkflow439] = useState<Workflow439 | null>(null);
  const [returnExclusionDrafts, setReturnExclusionDrafts] = useState<Record<number, boolean>>({});
  const [selectionSavedVersion, setSelectionSavedVersion] = useState(0);
  const [openingRestores, setOpeningRestores] = useState<string[]>([]);
  const [loadingDraftRows, setLoadingDraftRows] = useState<Record<string, unknown>[]>([]);
  const [decisionRecipeUpdates, setDecisionRecipeUpdates] = useState<RecipeUpdate[]>([]);
  const [purchaseDrafts, setPurchaseDrafts] = useState<Record<string, string>>({});
  useEffect(() => { setPurchaseDrafts({}); }, [refundCase.id, workflow439?.round]);
  useEffect(() => { setReturnExclusionDrafts({}); setOpeningRestores([]); setLoadingDraftRows([]); }, [refundCase.id]);
  const pendingPurchaseReviewScroll = useRef<string | null>(null);
  const pending439QuestionScroll = useRef<number | null>(null);
  const [refreshingResult, setRefreshingResult] = useState(false);
  const [automaticJob, setAutomaticJob] = useState<RefundWorkJob | null>(null);
  const [missingRecipe, setMissingRecipe] = useState<WorkflowQuestion | null>(null);
  const [questionFeedbackId, setQuestionFeedbackId] = useState<string | null>(null);
  const [editingRecipeQuestions, setEditingRecipeQuestions] = useState<string[]>([]);
  const trackRecipeEditing = useCallback((questionId: string, editing: boolean) => {
    setEditingRecipeQuestions(current => current.includes(questionId) === editing ? current
      : editing ? [...current, questionId] : current.filter(id => id !== questionId));
  }, []);
  const errorBannerRef = useRef<HTMLDivElement | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [sectorCode, setSectorCode] = useState(refundCase.reduced_rate_sector_code || "");
  const [subsectorCode, setSubsectorCode] = useState(refundCase.reduced_rate_subsector_code || "");
  const [sectorSaved, setSectorSaved] = useState(Boolean(refundCase.reduced_rate_sector_code));
  const sectorDraftEdited = useRef(false);
  useEffect(() => {
    if (workflow439?.saved_sector === undefined || sectorDraftEdited.current) return;
    const [sector, subsector = ""] = workflow439.saved_sector.split("/");
    setSectorCode(sector);
    setSubsectorCode(subsector);
    setSectorSaved(Boolean(sector));
  }, [workflow439?.saved_sector]);
  const [generalAllocation, setGeneralAllocation] = useState({ invoice_line_id: "", basis_kind: "turnover", numerator: "", denominator: "", explanation: "" });
  const [atikAllocation, setAtikAllocation] = useState({ invoice_line_id: "", asset_reference: "", use_start_period: refundCase.period_start.slice(0, 7), cumulative_refund_turnover: "", cumulative_total_turnover: "", opening_prior_allocated_vat: "" });
  const [vatFreeInputBase, setVatFreeInputBase] = useState(refundCase.declared_vat_free_input_base || "0");
  const [vatFreeConfirmed, setVatFreeConfirmed] = useState(Boolean(refundCase.vat_free_input_confirmed_at));
  const [manufacturerExplanation, setManufacturerExplanation] = useState("");
  const [legalIdentity, setLegalIdentity] = useState({ document_number: "", document_date: "", issuing_authority: "" });
  const [identitySelections, setIdentitySelections] = useState<Record<string, string>>({});
  const [editingRecipeKey, setEditingRecipeKey] = useState<string | null>(null);
  const [expandedRecipeKeys, setExpandedRecipeKeys] = useState<string[]>([]);
  const [recipeDraft, setRecipeDraft] = useState<RecipeComponentDraft[]>([]);
  const [recipeOutputUnit, setRecipeOutputUnit] = useState("");
  const [newRecipeProduct, setNewRecipeProduct] = useState("");
  const [materialMappings, setMaterialMappings] = useState<MaterialMappings | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [selectedPurchase, setSelectedPurchase] = useState("");
  const [smartMaterialResult, setSmartMaterialResult] = useState<SmartMaterialResult | null>(null);
  useEffect(() => { setSelectedOpeningRecords([]); }, [refundCase.id,
    workspace?.opening_stock_source?.lists.prior_input_vat?.id,
    workspace?.opening_stock_source?.lists.prior_loaded_vat?.id]);
  useEffect(() => {
    if (busy || !pendingPurchaseReviewScroll.current) return;
    const target = document.getElementById(`smart-purchase-matching-${pendingPurchaseReviewScroll.current}`);
    if (!target) return;
    // Wait for the new panel to render and the busy dialog to restore focus.
    pendingPurchaseReviewScroll.current = null;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [workflow439, busy]);
  useEffect(() => {
    if (busy || refreshingResult || automaticJob?.status !== "completed" || pending439QuestionScroll.current !== refundCase.id) return;
    if (!workflow439?.loading_review?.prepared || (!workflow439.questions.length && !workflow439.needs_choice)) {
      pending439QuestionScroll.current = null;
      return;
    }
    // Scroll only after the new questions render and the progress overlay closes.
    const frame = window.requestAnimationFrame(() => {
      pending439QuestionScroll.current = null;
      document.getElementById("smart-439-questions")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [workflow439, busy, refreshingResult, automaticJob?.status, refundCase.id]);
  useEffect(() => {
    setMaterialMappings(null); setSelectedMaterial(""); setSelectedPurchase("");
    setPurchaseSearch(""); setSmartMaterialResult(null);
  }, [refundCase.id]);
  useEffect(() => {
    if (automaticJob?.status !== "failed" || automaticJob.error_context?.code !== "439-question-answer") return;
    const ids = automaticJob.error_context.member_ids || [automaticJob.error_context.member_id];
    const members = (workflow439?.questions || []).flatMap(question => question.members || [question]);
    if (!ids.length || !ids.every(id => id && members.some(member => member.id === id
      && (purchaseDrafts[id] ?? member.answer) === "exclude"))) return;
    setError(current => current === automaticJob.error ? "" : current);
    setCalculationError(current => current === automaticJob.error ? "" : current);
    setAutomaticJob(null);
  }, [automaticJob, workflow439, purchaseDrafts]);
  const load = useCallback((preserveScreen = false, refreshSources = true) => {
    if (loadInFlightRef.current) return loadInFlightRef.current;
    const request = (async () => {
      const [workspaceData, identity, reconciliation] = await Promise.all([
        api<CapacityWorkspace & { workflow439?: Workflow439 }>(`/api/refund-cases/${refundCase.id}/capacity-workspace${refreshSources ? "" : "?display=1"}`),
        api<{ document_number: string; document_date: string; issuing_authority: string }>(`/api/refund-cases/${refundCase.id}/document-identity`),
        api<SourceReconciliation>(`/api/refund-cases/${refundCase.id}/source-reconciliation`).catch(() => null)
      ]);
      const preparation = workspaceData.workflow439 || null;
      setWorkspace(workspaceData);
      setLegalIdentity(identity);
      setSourceReconciliation(reconciliation);
      setWorkflow439(preparation);
      const latestJob = preparation?.active_job || preparation?.latest_job || null;
      setAutomaticJob(latestJob);
      setRefreshingResult(false);
      setMessage(current => current.startsWith("İşlem durumu geçici olarak alınamadı.")
        || current.startsWith("Hesaplama tamamlandı; sonuçlar ekrana yüklenemedi.") ? "" : current);
      if (!preserveScreen && latestJob?.status === "failed") setError(latestJob.error);
    })();
    loadInFlightRef.current = request;
    const clearRequest = () => {
      if (loadInFlightRef.current === request) loadInFlightRef.current = null;
    };
    void request.then(clearRequest, clearRequest);
    return request;
  }, [refundCase.id, refundCase.transaction_code, refundCase.claim_type, canPrepare]);

  useEffect(() => {
    if (!automaticJob || !["queued", "running"].includes(automaticJob.status)) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let statusNotice = "";
    const clearStatusNotice = () => {
      const previous = statusNotice;
      setMessage(current => current === previous ? "" : current);
    };
    const poll = async () => {
      let completed = false;
      try {
        const next = await api<RefundWorkJob>(`/api/refund-work-jobs/${automaticJob.id}`, { signal: AbortSignal.timeout(15000) });
        if (!active) return;
        if (next.status === "failed") {
          setAutomaticJob(next);
          setRefreshingResult(false);
          clearStatusNotice();
          setError(next.error || "Belge değerlendirmesi tamamlanamadı.");
          await load(true, false);
        } else if (next.status === "completed") {
          completed = true;
          setRefreshingResult(true);
          const current = await api<CapacityWorkspace & { workflow439?: Workflow439 }>(`/api/refund-cases/${refundCase.id}/capacity-workspace?display=1`);
          if (active) {
            pending439QuestionScroll.current = refundCase.id;
            setWorkspace(current);
            setWorkflow439(current.workflow439 || null);
            setAutomaticJob(next);
            setRefreshingResult(false);
            clearStatusNotice();
          }
        } else {
          setAutomaticJob(next);
          clearStatusNotice();
          timer = setTimeout(() => { void poll(); }, 3000);
        }
      } catch {
        if (active) {
          setRefreshingResult(false);
          // A failed status request does not mean the background job failed.
          // Retry the same job, and remove this notice when polling recovers.
          statusNotice = completed
            ? "Hesaplama tamamlandı; sonuçlar ekrana yüklenemedi. Otomatik olarak yeniden deneniyor; HESAPLA’ya tekrar basmanız gerekmiyor."
            : "İşlem durumu geçici olarak alınamadı. Otomatik olarak yeniden deneniyor; HESAPLA’ya tekrar basmanız gerekmiyor.";
          setMessage(statusNotice);
          timer = setTimeout(() => { void poll(); }, 5000);
        }
      }
    };
    timer = setTimeout(() => { void poll(); }, 1500);
    return () => { active = false; clearTimeout(timer); };
  }, [automaticJob?.id, refundCase.id]);

  useEffect(() => {
    load(false, false).catch((reason) => setError(reason instanceof Error ? reason.message : "Dosya okunamadı."));
  }, [load]);

  useEffect(() => {
    let active = true;
    let completed: number | null = null;
    let latestProgress: Partial<Record<UploadKind, UploadProgress>> = {};
    const refreshing: Partial<Record<UploadKind, UploadProgress>> = {};
    const unsubscribe = subscribeUploads(refundCase.id, (state) => {
      const finishedKinds = (Object.keys(latestProgress) as UploadKind[])
        .filter(kind => !state.progress[kind]);
      for (const kind of finishedKinds) refreshing[kind] = { ...latestProgress[kind]!, refreshing: true };
      latestProgress = state.progress;
      setUploadProgress({ ...refreshing, ...latestProgress });
      if (state.message) setMessage(state.message);
      if (state.error) setError(state.error);
      if (completed !== null && completed !== state.completed) {
        void (async () => {
          // Do not reuse a GET that began before the upload finished.
          await loadInFlightRef.current?.catch(() => undefined);
          if (active) {
            const preserveScreen = priorLedgerRefresh.current;
            priorLedgerRefresh.current = false;
            await load(preserveScreen, true);
          }
        })().catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : "Dosya yenilenemedi."); })
          .finally(() => {
            for (const kind of finishedKinds) delete refreshing[kind];
            if (active) setUploadProgress({ ...refreshing, ...latestProgress });
          });
      }
      completed = state.completed;
    });
    return () => { active = false; unsubscribe(); };
  }, [refundCase.id, load]);

  useEffect(() => {
    if (!error) return;
    if (questionFeedbackId && document.getElementById(`smart-question-${questionFeedbackId}`)) return;
    const frame = window.requestAnimationFrame(() => {
      errorBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      errorBannerRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [error, questionFeedbackId]);

  async function run(kind: string, operation: () => Promise<void>, success: string) {
    setQuestionFeedbackId(null);
    setBusy(kind);
    setError("");
    setMessage("");
    try {
      await operation();
      await load(false, true);
      setMessage(success);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "İşlem tamamlanamadı.");
    } finally {
      setBusy("");
    }
  }

  function runUpload<T>(
    kind: UploadKind,
    items: T[],
    uploadItem: (item: T) => Promise<void>,
    success: string | (() => string | Promise<string>),
    unit?: string
  ) {
    setError("");
    setMessage("");
    enqueueUpload(refundCase.id, kind, items, uploadItem, success, unit);
  }

  async function setSourcePreference(source: "recipe" | "opening", value: string) {
    setBusy("source-preference");
    setError("");
    try {
      const next = await api<CapacityWorkspace & { workflow439?: Workflow439 }>(`/api/refund-cases/${refundCase.id}/capacity-workspace`, {
        method: "POST", body: JSON.stringify({ action: "source_preference", source, value })
      });
      setWorkspace(next);
      setWorkflow439(next.workflow439 || null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Kaynak tercihi kaydedilemedi.");
    } finally { setBusy(""); }
  }

  function uploadCapacityRecipe(files: File[]) {
    const accepted = withExtensions(files, [".xlsx"]);
    if (accepted.length !== 1 || files.length !== 1) {
      setError("Şirket reçetesi için tek bir 2.RECETE_TEMPLATE.xlsx dosyası seçin.");
      return;
    }
    void runUpload("capacity-recipe", accepted, async (file) => {
      const payload = new FormData();
      payload.append("file", file);
      await api(`/api/refund-cases/${refundCase.id}/capacity-recipes/import`, { method: "POST", body: payload });
    }, "Şirket reçetesi yüklendi.");
  }

  async function setReturnExclusion(invoiceId: number, invoiceNo: string, exclude: boolean) {
    if (is439) {
      setReturnExclusionDrafts(current => ({ ...current, [invoiceId]: exclude }));
      setMessage(`${invoiceNo}: ${exclude ? "Kapsam dışı bırak" : "Kapsama geri al"} seçildi. Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.`);
      return;
    }
    let explanation = "";
    if (exclude) {
      if (refundCase.transaction_code === "439") {
        explanation = "İade faturasının asıl belgeyle ilişkisi doğrulanamadığı için kullanıcı bu dosyada kapsam dışında bıraktı.";
        if (!window.confirm(`${invoiceNo}: Asıl belgeyle ilişkisi doğrulanamadığı için bu dosyada kapsam dışında bırakılsın mı? Fatura silinmez; iade tutarı etkilenebilir.`)) return;
      } else {
        const answer = window.prompt(`${invoiceNo} yalnız bu iade dosyasının dışında bırakılacak; fatura silinmez. İade tutarı etkilenebilir. Gerekçenizi yazın:`);
        if (answer === null) return;
        explanation = answer.trim();
        if (explanation.length < 3) { setError("Kapsam dışı bırakma gerekçesini yazın."); return; }
      }
    }
    await run(`return-exclusion-${invoiceId}`, async () => {
      await api(`/api/refund-cases/${refundCase.id}/invoice-exclusions`, {
        method: exclude ? "POST" : "DELETE",
        body: JSON.stringify({ invoice_id: invoiceId, reason_code: "unresolved-return", explanation })
      });
    }, exclude ? `${invoiceNo} yalnız bu dosyada kapsam dışı bırakıldı. Hesap yeniden yapılmalı.` : `${invoiceNo} tekrar kontrollere dahil edildi.`);
  }

  function upload338Document(kind: "manufacturer_certificate" | "chamber_membership", files: File[]) {
    const uploadKind = kind === "manufacturer_certificate" ? "manufacturer" : "chamber";
    void runUpload(uploadKind, files, async (file) => {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("kind", kind);
      Object.entries(legalIdentity).forEach(([key, value]) => payload.append(key, value.trim()));
      if (kind === "manufacturer_certificate") {
        payload.append("production_confirmed", "true");
        payload.append("manufacturer_eligibility_confirmed", "true");
        payload.append("confirmation_explanation", manufacturerExplanation.trim());
      }
      await api(`/api/refund-cases/${refundCase.id}/documents`, { method: "POST", body: payload });
    }, kind === "manufacturer_certificate" ? "İmalatçı belgesi ve üretim ilişkisi teyidi kaydedildi." : "Meslek odası üyelik belgesi kaydedildi.");
  }

  function saveLegalIdentity() {
    void run("legal-identity", () => api(`/api/refund-cases/${refundCase.id}/document-identity`, {
      method: "PUT",
      body: JSON.stringify(legalIdentity)
    }), "Belge bilgileri kaydedildi.");
  }

  function uploadOpeningStock(files: File[]) {
    const accepted = withExtensions(files, [".xlsx"]);
    if (accepted.length !== 1 || files.length !== 1) {
      setError("Dönem başı stok için tek bir 1.STOK_TEMPLATE.xlsx seçin.");
      return;
    }
    void runUpload("opening", accepted, async (file) => {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("kind", "opening_stock");
      payload.append("refund_case", String(refundCase.id));
      const batch = await api<DataImportBatch>(`/api/taxpayers/${refundCase.taxpayer}/data-imports`, {
        method: "POST",
        body: payload
      });
      if (batch.status !== "succeeded") {
        throw new Error(batch.issues.map((issue) => typeof issue === "string" ? issue : issue.message || issue.detail || "").filter(Boolean).join(" ") || "Dönem başı stok dosyası kabul edilmedi.");
      }
      if (refundCase.transaction_code === "301") {
        await api(`/api/refund-cases/${refundCase.id}/301-loading`, {
          method: "POST",
          body: JSON.stringify({ action: "prepare_direct" })
        });
      }
    }, "Dönem başı stokları aktarıldı; FIFO bu stoklardan başlayacak şekilde yenilendi.");
  }

  function uploadPriorVat(kind: "prior_input_vat" | "prior_loaded_vat", files: File[]) {
    const accepted = withExtensions(files, [".xls", ".xlsx", ".csv"]);
    if (!accepted.length || accepted.length !== files.length || (kind === "prior_loaded_vat" && accepted.length !== 1)) {
      setError("İndirilecek için birleşik veya ayrı dönem listelerini; yüklenilen için tek liste seçin.");
      return;
    }
    void runUpload(kind, accepted, async (file) => {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("kind", kind);
      payload.append("refund_case", String(refundCase.id));
      await api<DataImportBatch>(`/api/taxpayers/${refundCase.taxpayer}/data-imports`, { method: "POST", body: payload });
    }, "Önceki dönem KDV listesi yüklendi.");
  }

  function uploadBom(files: File[], confirmedUnits: Record<string, string> = {}) {
    const accepted = withExtensions(files, [".xlsx"]);
    if (accepted.length !== 1 || files.length !== 1) {
      setError("Reçete için tek bir 2.RECETE_TEMPLATE.xlsx dosyasını seçin.");
      return;
    }
    setMissingBomUnits(null);
    void runUpload("bom", accepted, async (file) => {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("confirmed_material_units", JSON.stringify(confirmedUnits));
      try {
        await api(`/api/refund-cases/${refundCase.id}/recipes/import`, { method: "POST", body: payload });
      } catch (reason) {
        if (reason instanceof ApiError && Array.isArray(reason.body.issues)) {
          const missing = reason.body.issues.filter((issue): issue is { code: string; material_code: string; material_name: string } =>
            typeof issue === "object" && issue !== null && issue.code === "missing-new-material-unit");
          if (missing.length) setMissingBomUnits({ file, items: Array.from(new Map(missing.map(issue => {
            const key = issue.material_code || issue.material_name;
            return [key, { key, name: issue.material_name || issue.material_code, unit: "" }];
          })).values()) });
        }
        throw reason;
      }
    }, "Reçete/BOM verisi canonical mamul–girdi ilişkilerine aktarıldı.");
  }

  function confirmProductIdentity(groupKey: string) {
    const invoiceLineId = Number(identitySelections[groupKey]);
    if (!invoiceLineId) {
      setError("ERP ürün kodu için bir fatura ürünü seçin.");
      return;
    }
    void run(`product-identity-${groupKey}`, async () => {
      await api<{ recalculation_required: boolean }>(
        `/api/refund-cases/${refundCase.id}/product-identity-mappings`,
        { method: "POST", body: JSON.stringify({ group_key: groupKey, invoice_line_id: invoiceLineId }) }
      );
      setIdentitySelections((current) => {
        const next = { ...current };
        delete next[groupKey];
        return next;
      });
    }, "Ürün kodu eşleşmesi şirket sözlüğüne kaydedildi. Etkilenen 301/338 hesapları geçersizleştirildi; yüklenim ve iade hesabını yeniden hazırlayın.");
  }

  function loadingAction(action: string, values: Record<string, string>) {
    const success = action === "prepare_direct"
      ? "İhraç edilen satışlara ulaşan gerçek stok KDV izi hazırlandı."
      : "Yüklenim payı kaydedildi.";
    void run(`loading-${action}`, async () => {
      const response = await api<{ workspace: CapacityWorkspace }>(
        `/api/refund-cases/${refundCase.id}/301-loading`,
        { method: "POST", body: JSON.stringify({ action, ...values }) }
      );
      setWorkspace(response.workspace);
      if (action === "confirm_vat_free_input") setVatFreeConfirmed(true);
    }, success);
  }

  async function processReviewQueue() {
    if (reviewQueueRunningRef.current) return;
    reviewQueueRunningRef.current = true;
    while (reviewQueueRef.current.length) {
      const queued = reviewQueueRef.current.shift();
      if (!queued) continue;
      try {
        const response = await api<CapacityWorkspace>(
          `/api/refund-cases/${refundCase.id}/capacity-workspace`,
          {
            method: "POST",
            body: JSON.stringify({
              invoice_line_id: queued.item.invoice_line_id,
              finding_key: queued.item.finding_key,
              explanation: queued.explanation
            })
          }
        );
        setWorkspace(response);
      } catch (reason) {
        const detail = reason instanceof Error ? reason.message : "Açıklama kaydedilemedi.";
        setError(`${queued.item.invoice_no} numaralı faturadaki “${queued.item.item_name}” satırı kaydedilemedi: ${detail}`);
      } finally {
        setPendingReviewLineIds((lineIds) => (
          lineIds.filter((lineId) => lineId !== queued.item.invoice_line_id)
        ));
      }
    }
    reviewQueueRunningRef.current = false;
  }

  function submitExceptionReview(item: DirectLoadedVatCandidate) {
    const explanation = reviewNote.trim();
    if (savingReviewLineIds.includes(item.invoice_line_id)) return;
    const currentRow = document.getElementById(`capacity-exception-${item.invoice_line_id}`);
    const currentTop = currentRow?.getBoundingClientRect().top;
    const currentIndex = visibleExceptions.findIndex(
      (candidate) => candidate.invoice_line_id === item.invoice_line_id
    );
    setSavingReviewLineIds((lineIds) => [...lineIds, item.invoice_line_id]);
    setError("");
    setMessage("");
    setReviewNote("");

    window.setTimeout(() => {
      setPendingReviewLineIds((lineIds) => [...lineIds, item.invoice_line_id]);
      setSavingReviewLineIds((lineIds) => lineIds.filter((lineId) => lineId !== item.invoice_line_id));
      setReviewingLineId((lineId) => lineId === item.invoice_line_id ? null : lineId);
      reviewQueueRef.current.push({ item, explanation });
      void processReviewQueue();

      if (currentTop !== undefined) {
        requestAnimationFrame(() => {
          const rows = document.querySelectorAll<HTMLElement>(
            "#smart-required-reviews .smart-candidate-list > article"
          );
          const nextRow = rows[Math.min(Math.max(currentIndex, 0), rows.length - 1)];
          if (nextRow) window.scrollBy({ top: nextRow.getBoundingClientRect().top - currentTop });
        });
      }
    }, 120);
  }

  function saveSectorCode() {
    if (is439 && !workflow439?.sector_options?.some(option => option.value === [sectorCode, subsectorCode].filter(Boolean).join("/"))) {
      setError("İadeye konu faaliyeti listeden seçin.");
      return;
    }
    void run("sector", async () => {
      await api<RefundCase>(`/api/refund-cases/${refundCase.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          reduced_rate_sector_code: sectorCode.trim(),
          reduced_rate_subsector_code: subsectorCode.trim()
        })
      });
      setSectorSaved(true);
    }, "GİB sektör kodu kaydedildi; tekrar sorulmayacak.");
  }

  function setOpeningExclusions(recordIds: string[], exclude: boolean) {
    if (exclude) setSelectedOpeningRecords(current => [...new Set([...current, ...recordIds])]);
    else setOpeningRestores(current => [...new Set([...current, ...recordIds])]);
  }

  async function workflowAction(action: string, values: Record<string, unknown> = {}) {
    const questionId = typeof values.question_id === "string" ? values.question_id : null;
    if (questionId) setQuestionFeedbackId(workflow439?.questions.find(q => q.id === questionId || q.members?.some(m => m.id === questionId))?.id || questionId);
    else if (action !== "answer_members") setQuestionFeedbackId(null);
    if (action === "answer_members") {
      const ids = new Set(Object.keys(values.answers as Record<string, string>));
      const members = (workflow439?.questions || []).flatMap(q => q.members || [q]).filter(q => ids.has(q.id));
      const owner = workflow439?.questions.find(q => q.kind === "loading" && members.every(m => m.invoice_line_ids?.every(id => q.invoice_line_ids?.includes(id))));
      setQuestionFeedbackId(owner?.id || workflow439?.questions.find(q => (q.members || [q]).some(m => ids.has(m.id)))?.id || null);
    }
    setBusy("workflow-439");
    setError("");
    setMessage("");
    try {
      if (action === "review_loading" && loadInFlightRef.current) await loadInFlightRef.current;
      const next = await api<Workflow439>(`/api/refund-cases/${refundCase.id}/439-workflow`, {
        method: "POST", body: JSON.stringify({ action, ...values })
      });
      if (action === "review_loading" && !values.inline) pendingPurchaseReviewScroll.current = String(values.question_id);
      setWorkflow439(next);
      if (action === "business_mode") await load(false, true);
      if (action === "answer_members") {
        const savedIds = new Set(Object.keys(values.answers as Record<string, string>));
        setPurchaseDrafts(current => Object.fromEntries(Object.entries(current).filter(([id]) => !savedIds.has(id))));
      }
      if (action === "exclude_remaining" || action === "resume" || action === "review_type1" || action === "restore_recipe_product") {
        const job = await api<RefundWorkJob>(`/api/refund-cases/${refundCase.id}/prepare-allocations`, {
          method: "POST", headers: { Prefer: "respond-async" }, body: JSON.stringify({ automatic: true })
        });
        setAutomaticJob(job);
      }
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Cevap kaydedilemedi."); }
    finally { setBusy(""); }
  }

  async function calculate(mode: "draft" | "final") {
    setQuestionFeedbackId(null);
    setBusy(`calculate-${mode}`);
    setCalculationError("");
    setError("");
    setMessage("");
    try {
      if (is439) {
        for (const update of decisionRecipeUpdates) {
          await api(`/api/refund-cases/${refundCase.id}/capacity-recipes`, { method: "PUT", body: JSON.stringify(update) });
          setDecisionRecipeUpdates(current => current.filter(item => item.recipe_key !== update.recipe_key));
        }
        const answers = pendingPurchaseAnswers(workflow439?.questions || [], purchaseDrafts);
        if (Object.keys(answers).length || loadingDraftRows.length || !sectorSaved || selectedOpeningIds.length || openingRestores.length || Object.keys(returnExclusionDrafts).length) {
          await api<{ saved: boolean }>(`/api/refund-cases/${refundCase.id}/439-workflow`, {
            method: "POST", body: JSON.stringify({ action: "save_calculation_selections", answers,
              loading_rows: loadingDraftRows,
              opening_exclude: selectedOpeningIds, opening_restore: openingRestores, return_exclusions: returnExclusionDrafts,
              input_batch_id: workspace?.opening_stock_source?.lists.prior_input_vat?.id,
              loaded_batch_id: workspace?.opening_stock_source?.lists.prior_loaded_vat?.id,
              sector: sectorReady ? [sectorCode, subsectorCode].filter(Boolean).join("/") : undefined })
          });
          setWorkflow439(current => current ? { ...current, questions: current.questions.map(question => {
            if (!question.members) return answers[question.id] ? { ...question, answer: answers[question.id] } : question;
            const members = question.members.map(member => answers[member.id]
              ? { ...member, answer: answers[member.id] } : member);
            return { ...question, members, answer: members.every(member => member.answer)
              ? `custom:${JSON.stringify(Object.fromEntries(members.map(member => [member.id, member.answer])))}` : question.answer };
          }) } : current);
          setSelectionSavedVersion(current => current + 1);
          setReturnExclusionDrafts({}); setPurchaseDrafts({}); setLoadingDraftRows([]); setSelectedOpeningRecords([]); setOpeningRestores([]); setSectorSaved(sectorReady);
        }
        const job = await api<RefundWorkJob>(`/api/refund-cases/${refundCase.id}/prepare-allocations`, {
          method: "POST", headers: { Prefer: "respond-async" }, body: JSON.stringify({ automatic: true })
        });
        setAutomaticJob(job);
        if (job.status === "completed") {
          await load();
          pending439QuestionScroll.current = refundCase.id;
        }
        return;
      }
      let response = await api<CapacityCalculationResponse | CapacityPurchaseInvoiceSyncResponse>(
        `/api/refund-cases/${refundCase.id}/capacity-workspace/calculate`,
        { method: "POST", body: JSON.stringify({ mode }) }
      );
      let syncCycleCount = 0;
      while ("sync_job" in response) {
        syncCycleCount += 1;
        if (syncCycleCount > 12) {
          throw new Error("Alış faturası kapsamı art arda tamamlanamadı. Entegratör işlem ayrıntılarını kontrol edin.");
        }
        setWorkspace(response.workspace);
        setMessage("Eksik alış fatura dönemleri entegratörden yeniden getiriliyor. Başarılı dönemler korunacak; çekim bitince hesap kendiliğinden devam edecek.");
        let completedJob: InvoiceSyncJob | null = null;
        for (let attempt = 0; attempt < 300; attempt += 1) {
          await wait(2000);
          const job = await api<InvoiceSyncJob>(`/api/integrators/sync-jobs/${response.sync_job.id}`);
          if (job.status === "succeeded" || (job.status === "partial" && job.window_summary.failed === 0)) {
            completedJob = job;
            break;
          }
          if (job.status === "partial" || job.status === "failed") {
            throw new Error(job.error || "Alış faturalarının otomatik çekimi tamamlanamadı. Entegratör işlem ayrıntılarını kontrol edin.");
          }
        }
        if (!completedJob) {
          throw new Error("Alış faturalarının çekimi sürüyor. İşlem tamamlandığında İadeyi hesapla düğmesine yeniden basın.");
        }
        response = await api<CapacityCalculationResponse | CapacityPurchaseInvoiceSyncResponse>(
          `/api/refund-cases/${refundCase.id}/capacity-workspace/calculate`,
          {
            method: "POST",
            body: JSON.stringify({ mode, invoice_sync_job_id: completedJob.id })
          }
        );
      }
      setWorkspace(response.workspace);
      setMessage(mode === "draft" ? "Taslak hesap oluşturuldu; hiçbir nihai liste veya KDV kullanımı kaydedilmedi." : "Nihai KDV iade hesabı ve listeleri oluşturuldu.");
      if ((response.workspace.latest_result?.issues || []).some((item) => item.severity === "error")) {
        window.setTimeout(() => scrollTo("smart-action-required"), 0);
      }
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Hesap tamamlanamadı.";
      setCalculationError(message);
      setError(message);
      await load();
      window.setTimeout(() => scrollTo("smart-action-required"), 0);
    } finally {
      setBusy("");
    }
  }

  function reconcileSources() {
    void run("reconcile-sources", async () => {
      setSourceReconciliation(await api<SourceReconciliation>(`/api/refund-cases/${refundCase.id}/source-reconciliation`, { method: "POST" }));
    }, "Fatura, defter/muavin, stok ve KDV1 kaynakları yeniden karşılaştırıldı.");
  }

  async function loadMaterialMappings() {
    const next = await api<MaterialMappings>(`/api/refund-cases/${refundCase.id}/recipe-material-mappings`);
    setMaterialMappings(next);
    const materialId = next.materials.some(item => String(item.id) === selectedMaterial) ? selectedMaterial : refundCase.transaction_code === "439" ? String(next.materials[0]?.id || "") : "";
    setSelectedMaterial(materialId);
    setSelectedPurchase(proposedPurchase(next, materialId));
  }

  function proposedPurchase(mapping: MaterialMappings, materialId: string) {
    if (refundCase.transaction_code !== "439") return "";
    const material = mapping.materials.find(item => String(item.id) === materialId);
    const candidates = mapping.candidates.filter(item => material && item.base_unit === material.base_unit && (
      item.code.toLocaleLowerCase("tr-TR") === material.code.toLocaleLowerCase("tr-TR") || item.name.toLocaleLowerCase("tr-TR") === material.name.toLocaleLowerCase("tr-TR")
    ));
    return candidates.length === 1 ? String(candidates[0].id) : "";
  }

  function openMaterialMappings() {
    void run("load-material-mappings", loadMaterialMappings, "Eşleşmeyen reçete hammaddeleri açıldı.");
  }

  function matchMaterial(smart: boolean) {
    void run(smart ? "smart-material" : "manual-material", async () => {
      setSmartMaterialResult(null);
      if (smart) {
        const result = await api<SmartMaterialResult>(`/api/refund-cases/${refundCase.id}/recipe-material-mappings/smart`, {
          method: "POST", body: JSON.stringify({ material: Number(selectedMaterial) })
        });
        setSmartMaterialResult(result);
      } else {
        await api(`/api/refund-cases/${refundCase.id}/recipe-material-mappings`, {
          method: "POST", body: JSON.stringify({ material: Number(selectedMaterial), purchase_product: Number(selectedPurchase), match_source: "manual" })
        });
      }
      await loadMaterialMappings();
    }, smart ? "Akıllı eşleştirme sonucu aşağıda. Kaydedilen eşleşme otomatik hesaba alınır." : "Manuel eşleşme kaydedildi; hesap otomatik güncellenir.");
  }

  function undoSmartMaterial() {
    if (!smartMaterialResult?.applied) return;
    void run("undo-smart-material", async () => {
      await api(`/api/refund-cases/${refundCase.id}/recipe-material-mappings`, {
        method: "DELETE", body: JSON.stringify({ materials: [smartMaterialResult.material_id] })
      });
      setSmartMaterialResult(null);
      await loadMaterialMappings();
    }, "Akıllı eşleşme geri alındı. Hammadde yeniden manuel eşleştirmede.");
  }

  function editRecipe(recipeKey: string) {
    const draft = (workspace?.capacity_recipes || [])
      .filter((item) => item.recipe_key === recipeKey)
      .map((item) => ({
        draft_key: item.component_key,
        component_key: item.component_key,
        material_code: item.material_code,
        material_name: item.material_name,
        material_unit: item.material_unit,
        material_quantity: item.material_quantity
      }));
    setRecipeDraft(draft);
    setRecipeOutputUnit(workspace?.capacity_recipes.find(item => item.recipe_key === recipeKey)?.output_unit || "");
    setEditingRecipeKey(recipeKey);
  }

  function addRecipeComponent() {
    const materials = (workspace?.capacity_report?.analysis.inputs || [])
      .filter((item) => item.economic_role === "direct_material");
    const material = materials.find((item) => !recipeDraft.some((row) => (
      row.material_code === item.code
      && row.material_name === item.name
      && row.material_unit === item.unit
    )));
    if (!material) {
      setError("Kapasite raporundaki tüm doğrudan hammaddeler bu reçetede kullanılıyor.");
      return;
    }
    setRecipeDraft((current) => [...current, {
      draft_key: `new-${Date.now()}-${current.length}`,
      component_key: "",
      material_code: material.code,
      material_name: material.name,
      material_unit: material.unit,
      material_quantity: "1"
    }]);
  }

  function changeDraftMaterial(draftKey: string, identity: string) {
    const material = (workspace?.capacity_report?.analysis.inputs || []).find((item) => (
      `${item.code}|${item.name}|${item.unit}` === identity
    ));
    if (!material) return;
    setRecipeDraft((current) => current.map((row) => row.draft_key === draftKey ? {
      ...row,
      material_code: material.code,
      material_name: material.name,
      material_unit: material.unit
    } : row));
  }

  async function saveDecisionRecipes(updates: RecipeUpdate[]) {
    setBusy("save-recipe-quantities");
    try {
      for (const update of updates) {
        const saved = await api<CapacityWorkspace>(`/api/refund-cases/${refundCase.id}/capacity-recipes`, {
          method: "PUT", body: JSON.stringify(update)
        });
        setWorkspace(current => current ? { ...current, capacity_recipes: saved.capacity_recipes,
          purchase_material_recipes: saved.purchase_material_recipes } : current);
        setDecisionRecipeUpdates(current => current.filter(item => item !== update));
      }
    } finally {
      setBusy("");
    }
  }

  function saveRecipe(recipeKey: string) {
    void run("save-capacity-recipe", async () => {
      await api(`/api/refund-cases/${refundCase.id}/capacity-recipes`, {
        method: "PUT",
        body: JSON.stringify({ recipe_key: recipeKey, components: recipeDraft, output_unit: recipeOutputUnit })
      });
      setEditingRecipeKey(null);
      setRecipeDraft([]);
    }, "Reçete kaydedildi; kapasite kontrolleri ve sonraki iade hesabı güncel miktarları kullanacak.");
  }

  function createRecipe() {
    const product = (workspace?.capacity_report?.analysis.outputs || []).find((item) => (
      `${item.code}|${item.name}|${item.unit}` === newRecipeProduct
    ));
    if (!product) return;
    void run("create-capacity-recipe", async () => {
      await api(`/api/refund-cases/${refundCase.id}/capacity-recipes`, {
        method: "POST",
        body: JSON.stringify({
          product_code: product.code,
          product_name: product.name,
          output_unit: product.unit,
          components: recipeDraft
        })
      });
      setEditingRecipeKey(null);
      setNewRecipeProduct("");
      setRecipeDraft([]);
    }, "Yeni reçete kaydedildi ve sonraki iade hesabına dahil edildi.");
  }

  function deleteRecipe(recipeKey: string, productName: string) {
    if (!window.confirm(`“${productName}” reçetesi tamamen silinsin mi?`)) return;
    void run("delete-capacity-recipe", async () => {
      await api(`/api/refund-cases/${refundCase.id}/capacity-recipes`, {
        method: "DELETE",
        body: JSON.stringify({ recipe_key: recipeKey })
      });
      setEditingRecipeKey(null);
      setRecipeDraft([]);
    }, "Reçete silindi; kapasite kontrolleri ve sonraki iade hesabı güncellendi.");
  }

  const summary = workspace?.summary;
  const analysis = workspace?.capacity_report?.analysis;
  const companyRecipeEditing = Boolean(workspace?.capacity_recipes.some(row => row.recipe_key.startsWith("company-source:")));
  const is301 = refundCase.transaction_code === "301";
  const is338 = refundCase.transaction_code === "338";
  const isAnnualReducedRate = refundCase.claim_type === "reduced_rate_annual";
  const is439 = isAnnualReducedRate && refundCase.transaction_code === "439";
  const tradeOnly = is439 && workflow439?.production_requirement.mode === "trade";
  const requiresExportChain = is301 || is338;
  const manufacturerBlockers = workspace?.manufacturer_readiness?.blockers || [];
  const noGeneralConfirmed = Boolean(workspace?.loading_engine?.confirmed_none.general);
  const noAtikConfirmed = Boolean(workspace?.loading_engine?.confirmed_none.atik);
  const missingPurchaseEvidence = Boolean(
    refundCase.transaction_code === "439"
    && summary
    && !summary.purchase_invoice_source_complete
  );
  const invoicePresentation = invoiceSourcePresentation(refundCase.transaction_code, summary, {
    declaration: Boolean(uploadProgress.declaration),
    ledger: Boolean(uploadProgress.ledger),
  });
  const completionChecks = requiresExportChain ? [
    Boolean((summary?.incoming_invoice_count || 0) + (summary?.outgoing_invoice_count || 0)),
    Boolean(summary?.export_chain_ready),
    Boolean(summary?.declarations_ready),
    Boolean(summary?.ledger_ready),
    is301 ? Boolean(workspace?.loading_engine?.ready) : Boolean(workspace?.manufacturer_readiness?.ready)
  ] : is439 ? [
    Boolean((summary?.incoming_invoice_count || 0) + (summary?.outgoing_invoice_count || 0)) && !missingPurchaseEvidence,
    Boolean(summary?.declarations_ready),
    Boolean(summary?.ledger_ready),
    tradeOnly || Boolean(workspace?.capacity_report || workspace?.capacity_recipe_import),
    Boolean(workspace?.opening_stock_source?.ready)
  ] : [
    Boolean((summary?.incoming_invoice_count || 0) + (summary?.outgoing_invoice_count || 0)) && !missingPurchaseEvidence,
    Boolean(summary?.declarations_ready),
    Boolean(summary?.ledger_ready),
    Boolean(workspace?.capacity_report)
  ];
  const completed = completionChecks.filter(Boolean).length;
  const progress = Math.round(completed * 100 / completionChecks.length);
  const capacityIssues = workspace?.warnings || [];
  const usesOfficialLoadedVat = Boolean(summary?.official_loaded_vat_count);
  const reportCapacityIssues = capacityIssues.filter((item) => item.code.startsWith("capacity-report-"));
  const recipeCapacityIssues = capacityIssues.filter((item) => [
    "capacity-relation-exceeded",
    "capacity-relation-missing",
    "capacity-sale-link-missing",
    "capacity-output-unmatched"
  ].includes(item.code));
  const hardCapacityIssues = usesOfficialLoadedVat ? [] : capacityIssues.filter((item) => (
    item.severity === "error"
    && (item.code === "capacity-output-exceeded" || item.code === "capacity-input-exceeded")
  ));
  const visibleCandidates = useMemo(
    () => (workspace?.direct_loaded_vat || []).slice(0, 50),
    [workspace?.direct_loaded_vat]
  );
  const candidateIssueGroups = useMemo(() => {
    const groups = new Map<string, DirectLoadedVatCandidate[]>();
    for (const item of workspace?.direct_loaded_vat || []) {
      for (const issue of item.issues) groups.set(issue, [...(groups.get(issue) || []), item]);
    }
    return Array.from(groups.entries()).map(([issue, items]) => ({ issue, items }));
  }, [workspace?.direct_loaded_vat]);
  const capacityRecipes = useMemo(() => {
    const grouped = new Map<string, CapacityWorkspace["capacity_recipes"]>();
    for (const item of workspace?.capacity_recipes || []) {
      const current = grouped.get(item.recipe_key) || [];
      current.push(item);
      grouped.set(item.recipe_key, current);
    }
    return Array.from(grouped.entries()).map(([recipeKey, components]) => ({
      recipeKey,
      components,
      product: components[0]
    }));
  }, [workspace?.capacity_recipes]);
  const pendingReviewLineIdSet = useMemo(
    () => new Set(pendingReviewLineIds),
    [pendingReviewLineIds]
  );
  const visibleExceptions = useMemo(
    () => (workspace?.exceptions || [])
      .filter((item) => !pendingReviewLineIdSet.has(item.invoice_line_id))
      .slice(0, 50),
    [workspace?.exceptions, pendingReviewLineIdSet]
  );
  const sectorReady = !isAnnualReducedRate || (is439 ? Boolean(workflow439?.sector_options?.some(option => option.value === [sectorCode, subsectorCode].filter(Boolean).join("/"))) : sectorSaved);
  const anyUploadBusy = Object.keys(uploadProgress).length > 0;
  const generalCandidates = (workspace?.invoice_analysis || []).filter((item) => item.load_type === "general" && Number(item.vat_amount) > 0);
  const atikCandidates = (workspace?.invoice_analysis || []).filter((item) => item.load_type === "atik" && Number(item.vat_amount) > 0);
  const kdv1SourceItems = useMemo(() => (sourceReconciliation?.items || []).filter((item) => {
    if (item.code !== "kdv1-source-reconciliation" || !item.period || item.status === "matched") return false;
    const mismatches = Array.isArray(item.details.mismatches) ? item.details.mismatches : [];
    return mismatches.includes("reduced_base")
      || mismatches.includes("reduced_vat")
      || mismatches.includes("current_deductible_invoice")
      || mismatches.includes("current_deductible_accounting");
  }), [sourceReconciliation]);
  const kdv1SalesItems = useMemo(() => kdv1SourceItems.filter((item) => {
    const mismatches = Array.isArray(item.details.mismatches) ? item.details.mismatches : [];
    return mismatches.includes("reduced_base") || mismatches.includes("reduced_vat");
  }), [kdv1SourceItems]);
  const resolvedSalesIssueCodes = useMemo(() => new Set(kdv1SalesItems
    .filter((item) => item.status === "resolved" && item.period)
    .map((item) => `sales-reconciliation:${item.period?.slice(0, 7).replace("-", "")}`)), [kdv1SalesItems]);
  const resultIssues = useMemo(() => {
    const issues = [
      ...(workspace?.latest_result?.issues || []),
      ...(workspace?.latest_result?.blockers || []).map(item => ({ ...item, severity: "error" as const })),
    ];
    if (is439) return Array.from(new Map(issues
      .filter(item => !resolvedSalesIssueCodes.has(item.code))
      .map(item => [item.message, item])).values());
    const salesPeriods: string[] = [];
    const returnKdv1Periods: string[] = [];
    const returnKdv1InvoiceIds = new Set<number>();
    const unique = new Map<string, (typeof issues)[number]>();
    for (const item of issues) {
      const period = item.code.match(/^sales-reconciliation:(\d{4})(\d{2})$/);
      if (period) {
        if (resolvedSalesIssueCodes.has(item.code)) continue;
        salesPeriods.push(`${period[2]}.${period[1]}`);
        continue;
      }
      const returnKdv1Period = item.code.match(/^return-kdv1-additional-vat-(?:missing|short):(\d{4})(\d{2})$/);
      if (returnKdv1Period) {
        returnKdv1Periods.push(`${returnKdv1Period[2]}.${returnKdv1Period[1]}`);
        const ids = Array.isArray(item.metadata?.return_invoice_ids)
          ? item.metadata.return_invoice_ids
          : [];
        ids.map(Number).filter(Boolean).forEach((id) => returnKdv1InvoiceIds.add(id));
        continue;
      }
      unique.set(item.message, item);
    }
    if (salesPeriods.length && !kdv1SalesItems.length) {
      unique.set("sales-reconciliation", {
        code: "sales-reconciliation",
        severity: "error",
        message: `KDV1 ile indirimli oran satış toplamı ${salesPeriods.join(", ")} dönemlerinde farklı. Ayrı ayrı tekrarlanan ${salesPeriods.length} mesaj tek kontrolde toplandı.`
      });
    }
    if (returnKdv1Periods.length) {
      unique.set("return-kdv1-additional-vat", {
        code: "return-kdv1-additional-vat",
        severity: "error",
        message: `${returnKdv1Periods.join(", ")} dönemlerinin KDV1 beyannamelerinde alış iadesine ait “İlave Edilecek KDV” alanı doğrulanamadı.`,
        metadata: {
          periods: returnKdv1Periods,
          invoice_ids: Array.from(returnKdv1InvoiceIds)
        }
      });
    }
    return Array.from(unique.values());
  }, [is439, kdv1SalesItems.length, resolvedSalesIssueCodes, workspace?.latest_result?.issues, workspace?.latest_result?.blockers]);

  if (!workspace) return error ? <div className="loading">
    <p>{error}</p>
    <button className="primary-button compact" type="button" onClick={() => {
      setError("");
      void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Dosya okunamadı."));
    }}>Tekrar dene</button>
  </div> : <div className="loading">KDV iadesi hazırlanıyor…</div>;

  const missingSources = [
    !((summary?.incoming_invoice_count || 0) + (summary?.outgoing_invoice_count || 0)) && "faturaları",
    requiresExportChain && !summary?.export_chain_ready && "GÇB/ETGB kaynak zincirini",
    !summary?.declarations_ready && `${(summary?.missing_declaration_periods || []).join(", ") || "eksik"} KDV1 beyannamesini`,
    !is338 && !summary?.ledger_ready && `${(summary?.missing_ledger_periods || []).join(", ") || "eksik"} e-Defter / muavin dönemini`,
    !requiresExportChain && !tradeOnly && !workspace?.capacity_report && !workspace?.capacity_recipe_import && "kapasite raporu veya reçete Excel’ini",
    is439 && !workspace.opening_stock_source?.ready && "açılış stok kaynağını"
  ].filter((item): item is string => Boolean(item));
  const pendingReviewCount = (workspace.exceptions || []).filter(
    (item) => pendingReviewLineIdSet.has(item.invoice_line_id)
  ).length;
  const reviewCount = Math.max(0, (summary?.review_count || 0) - pendingReviewCount);
  const backgroundReviewCount = pendingReviewLineIds.length + savingReviewLineIds.length;
  const backgroundActionCount = backgroundReviewCount;
  const automaticPreparationBusy = automaticJob?.status === "queued" || automaticJob?.status === "running";
  const interruptedCalculation = automaticJob?.status === "failed" && automaticJob.error_context?.code === "worker_interrupted";
  const openingReviewItems = workspace.opening_stock_source?.review_items || [];
  const openingError = (value: string) => openingReviewItems.length && (value.includes("önceki dönem alış faturası") || value.includes("indirilecek listesinde çelişen"))
    ? `Açılış stok kontrolünde ${openingReviewItems.length} kayıt çözüm bekliyor. Doğrulanan stok kayıtları korundu. Ayrıntıları Açılış stok kontrolü bölümünden inceleyin.` : value;
  const excludedOpeningItems = workspace.opening_stock_source?.excluded_items || [];
  const openingScopeBusy = !canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy;
  const selectedOpeningIds = openingReviewItems.filter(item => selectedOpeningRecords.includes(item.id)).map(item => item.id);
  const missingRecipeQuestions = (workflow439?.questions || []).flatMap(question => question.members || [question]).filter(question => question.kind === "recipe_source" && !question.applied);
  const rawErrorContext = automaticJob?.status === "failed" && automaticJob.error === error ? automaticJob.error_context : null;
  const errorQuestion = rawErrorContext?.invoice_line_id && workflow439?.questions.find(q =>
    !q.applied && (q.members || [q]).some(member => member.invoice_line_ids?.includes(rawErrorContext.invoice_line_id!)));
  const errorMember = errorQuestion && (errorQuestion.members || [errorQuestion]).find(member => member.invoice_line_ids?.includes(rawErrorContext!.invoice_line_id!));
  const errorContext = rawErrorContext && !rawErrorContext.question_id && errorQuestion && errorMember
    ? { ...rawErrorContext, question_id: errorQuestion.id, question_ids: [errorQuestion.id], member_id: errorMember.id, member_ids: [errorMember.id] }
    : rawErrorContext;
  const blockingWarningCount = summary?.blocking_warning_count || 0;
  const unresolvedKdv1SourceCount = kdv1SourceItems.filter((item) => item.status !== "resolved").length;
  const actionResultIssues = resultIssues.filter((item) => item.severity === "error");
  const unresolvedReturnIds = unresolvedReturnInvoiceIds(workspace.latest_result)
    .filter(id => !workspace.excluded_return_invoices?.some(row => row.invoice_id === id));
  const pendingReturnExclusions = unresolvedReturnIds.filter(id => returnExclusionDrafts[id] !== true);
  const purchaseExclusionQuestions = (workflow439?.questions || []).flatMap(question => question.members || [question])
    .filter(question => question.kind === "loading" && !question.applied
      && question.options.some(option => option.value === "exclude" && !option.disabled));
  const purchaseExclusionCount = new Set(purchaseExclusionQuestions.flatMap(question => question.invoice_line_ids || [])).size;
  const showBulkPurchaseExclusion = is439 && purchaseExclusionCount > 0
    && purchaseExclusionCount === summary?.loading_pending_count
    && actionResultIssues.some(item => item.code === "pending-loading-decisions");
  const purchaseExclusionsSelected = purchaseExclusionQuestions.every(question => (purchaseDrafts[question.id] || question.answer) === "exclude");
  const showActionRequired = Boolean(
    unresolvedKdv1SourceCount || hardCapacityIssues.length || actionResultIssues.length
  );
  const kdv1Issues = requiresExportChain ? workspace.kdv1_reconciliation?.issues || [] : [];
  const productionIssues = is301 ? workspace.production_readiness?.issues || [] : [];
  const loadingReviewNeedsRefresh = Boolean(is439 && workflow439?.loading_review?.prepared
    && (workflow439.loading_review.stale || workflow439.loading_review.rows.some(row => row.stale)));
  const reviewing23 = is439 && loadingReviewNeedsPreparation(workflow439?.loading_review);
  const loadingReviewPending = Boolean(reviewing23 && workflow439?.loading_review?.prepared && !loadingReviewNeedsRefresh);
  const pendingPurchaseDrafts = pendingPurchaseAnswers(workflow439?.questions || [], purchaseDrafts);
  const current439Check = !reviewing23 && is439 && !Object.keys(pendingPurchaseDrafts).length && !loadingDraftRows.length && !decisionRecipeUpdates.length
    && !selectedOpeningIds.length && !openingRestores.length && !Object.keys(returnExclusionDrafts).length
    && [sectorCode, subsectorCode].filter(Boolean).join("/") === workflow439?.saved_sector
    && workflow439?.calculation_required === false && !automaticPreparationBusy && automaticJob?.status !== "failed";
  const loadingRowsById = new Map(workspace.invoice_analysis.map(row => [row.invoice_line_id, row]));
  const guidedQuestions = (workflow439?.questions || []).filter(question => {
    if (question.id.startsWith("source:") && resolvedSalesIssueCodes.has(question.id.slice("source:".length))) return false;
    if (!workspace.latest_result || !["source:stock-fifo-unit-conversion", "source:stock-fifo-shortage"].includes(question.id)) return true;
    return actionResultIssues.some(issue => `source:${issue.code}` === question.id);
  }).map(question => {
    const summary = question.kind === "loading" && getLoadingAction(question) === "recipe"
      ? loadingReviewSummary(question, workspace.invoice_analysis) : undefined;
    const count = question.invoice_line_ids?.length || 0;
    return { ...question,
      checked: current439Check && question.checked,
      loading_summary: summary,
      missing_recipe_count: summary?.reasons.length === 1 && ["recipe", "recipe_link"].includes(summary.reasons[0].code)
        ? new Set(missingLoadingRecipes(workspace.invoice_analysis.filter(row => question.invoice_line_ids?.includes(row.invoice_line_id)),
            workspace.capacity_recipes).map(row => row.recipe_key)).size : undefined,
      records: question.records?.map(row => ({ ...row,
        review_reason: row.invoice_line_id ? loadingRowsById.get(row.invoice_line_id)?.recipe_check?.reason : undefined,
      })),
      options: summary?.consumptionExplained ? question.options.map(option => ({ ...option,
        label: option.value === "exclude" ? `Kontrol ettim; bu ${count} satırın KDV’sini bu iade hesabına dahil etme`
          : option.value === "defer" ? "Henüz karar veremiyorum; bu satırlar açık kalsın"
          : option.value === "recheck_sources" ? "Kaynak veya eşleştirme bilgilerini düzelttim; yeniden hesapla" : option.label,
      })) : question.options,
    };
  }).filter(question => (question.kind === "loading" && actionResultIssues.some(item => item.code === "pending-loading-decisions"))
    || question.missing_recipe_count !== 0 && hasActionableStockInformation(question, workspace.invoice_analysis));
  const pendingQuestions = guidedQuestions.filter(question => !question.applied && !question.optional
    && (question.members || [question]).some(member => !member.applied && !member.optional));
  const invalidPurchaseAnswers = (workflow439?.questions || []).flatMap(question => question.members || [question])
    .filter(question => !question.applied && (
      Object.prototype.hasOwnProperty.call(purchaseDrafts, question.id)
        && purchaseDrafts[question.id] !== question.answer && !pendingPurchaseDrafts[question.id]
      || question.kind === "capacity_material" && question.answer && !pendingPurchaseDrafts[question.id]
        && !question.options.some(option => option.value === question.answer && !option.disabled)));
  const openingSourcesUploaded = Boolean(workspace.opening_stock_source?.ready ||
    (workspace.opening_stock_source?.lists.prior_input_vat && workspace.opening_stock_source?.lists.prior_loaded_vat));
  const required439SourcesReady = !is439 || Boolean(summary?.incoming_invoice_count && summary?.declarations_ready && summary?.ledger_ready && (tradeOnly || workspace.capacity_report || workspace.capacity_recipe_import) &&
    (reviewing23 ? openingSourcesUploaded : workspace.opening_stock_source?.ready || (openingReviewItems.length > 0 && openingReviewItems.length === selectedOpeningIds.length && workspace.opening_stock_source?.issues.length === openingReviewItems.length)));
  const calculationReady = required439SourcesReady && !current439Check && (is439
    ? reviewing23 || (sectorReady && Boolean(workflow439?.production_requirement.ready) && (Boolean(workflow439?.material_review?.requested) || (!workflow439?.needs_choice && !workflow439?.material_review?.required)))
    : workspace.can_calculate && sectorReady) && backgroundActionCount === 0 && !automaticPreparationBusy && (reviewing23 || (!missingRecipe && editingRecipeQuestions.length === 0));
  const calculationBasisMissing = Boolean(workspace.latest_result?.summary.independent_basis_missing);
  const calculationPending = busy.startsWith("calculate-") || automaticPreparationBusy;
  const calculationFailure = openingError(calculationError || (automaticJob?.status === "failed" ? automaticJob.error : ""));
  const calculatedAmount = calculationAmount(workspace.latest_result, isAnnualReducedRate ? "maximum_claimable_amount" : "requested_amount");
  const provisionalResult = is439 ? provisionalCalculation(workspace.latest_result) : null;
  const calculationStatus = busy.startsWith("calculate-")
    ? "Hesaplanıyor"
    : anyUploadBusy
    ? "Kaynaklar işleniyor"
    : automaticPreparationBusy
    ? "Hesap güncelleniyor"
    : backgroundActionCount
    ? `${backgroundActionCount} kontrol kaydediliyor`
    : is439 && !sectorReady && !reviewing23
    ? "GİB sektör seçimi gerekli"
    : is439 && workspace.latest_result?.status === "blocked"
    ? "İade tutarı hesaplanamadı"
    : is439
    ? provisionalResult ? "Geçici hesap hazır" : workflow439?.needs_choice ? "Devam tercihiniz bekleniyor" : current439Check && workflow439?.questions.length ? "Kontrol tamamlandı; eksik bilgiler var" : workflow439?.questions.some(q => !q.answer) ? "Cevaplarınız bekleniyor" : calculationBasisMissing || workspace.latest_result?.status === "blocked" ? "İade tutarı henüz hesaplanamadı" : workspace.latest_result?.status === "limited" ? "Seçilen kapsam hesaplandı" : workspace.latest_result?.status === "completed" ? "İade hesaplandı" : "Hesaplamaya hazır"
    : unresolvedKdv1SourceCount
    ? "KDV1 kaynak farkı var"
    : workspace.latest_result?.status === "final"
    ? "Nihai hesap hazır"
    : workspace.latest_result?.status === "completed"
    ? "İade hesaplandı"
    : workspace.latest_result?.status === "blocked"
    ? "Hesaplama tamamlanamadı"
    : workspace.latest_result?.status === "draft"
    ? "Taslak hesaplandı"
    : kdv1Issues.length
    ? "KDV1 farkı var"
    : !sectorReady
    ? "Sektör kodu gerekli"
    : missingSources.length
    ? "Eksik belge var"
    : blockingWarningCount || reviewCount || !workspace.can_calculate
    ? "Kontrol gerekiyor"
    : "Hesaplamaya hazır";
  const calculationInstruction = anyUploadBusy
    ? "Yüklemelerin tamamlanmasını bekleyin."
    : automaticPreparationBusy
    ? "Kaydettiğiniz kararlar ve eşleştirmeler arka planda değerlendiriliyor. Sonuç hazır olduğunda bu alan güncellenecek; yeniden onay vermeniz gerekmiyor."
    : backgroundActionCount
    ? `${backgroundActionCount} kontrol arka planda kaydediliyor; kontrole devam edebilirsiniz.`
    : provisionalResult
    ? "Doğrulanmış kayıtların geçici sonucu aşağıda. Açık alışların tüketim veya satış/stok bilgilerini tamamladıktan sonra yeniden hesaplayın."
    : is439 && workspace.latest_result?.status === "blocked"
    ? "İade tutarı hesaplanamadı. Hesabı durduran kaynak kontrolleri ve ilgili işlem düğmeleri ‘Eylem gerekli’ bölümünde gösteriliyor."
    : current439Check
    ? workflow439?.questions.length
      ? pendingQuestions.length ? `${pendingQuestions.length} zorunlu grupta karar bekleniyor. Aşağıdaki zorunlu işlerin düzenleme alanlarını kullanın.` : "Mevcut kaynaklar ve cevaplar için kontrol tamamlandı."
      : workspace.latest_result?.status === "blocked"
        ? "Son kontrol tamamlandı. İade tutarını engelleyen nedenler aşağıda gösteriliyor; ilgili kaynak bilgilerini düzelttikten sonra yeniden hesaplayabilirsiniz."
        : "Mevcut kaynaklar ve cevaplarla hesaplama tamamlandı. Kaynak veya cevap değişirse yeniden hesaplayabilirsiniz."
    : is439 && !required439SourcesReady
    ? `Faturalar, beyannameler ve e-Defter veya muavin zorunludur.${tradeOnly ? "" : " Kapasite raporu veya reçete Excel’ini de yükleyin."} Ayrıca stok Excel’i veya önceki dönemin iki KDV listesini tamamlayın.`
    : is439
    ? !workflow439?.production_requirement.ready ? workflow439?.production_requirement.message || "Üretim dayanağını yükleyin." : workflow439.needs_choice ? "Yukarıdan ek soruları cevaplama veya kalan belirsizlikleri kapsam dışında bırakma tercihinizi seçin." : workflow439.questions.some(q => !q.answer) ? "Yukarıdaki soru gruplarını cevaplayın; ardından HESAPLA’ya basın." : "HESAPLA’ya bastığınızda kaydettiğiniz cevaplar uygulanır ve sonuç güncellenir."
    : !sectorReady
    ? is439 ? "İadeye konu faaliyeti listeden seçip Kaydet'e basın." : "Sektör kodunu girip Kaydet'e basın."
    : missingSources.length
    ? `${missingSources.join(", ")} yükleyin.`
    : unresolvedKdv1SourceCount
    ? `${unresolvedKdv1SourceCount} dönemdeki KDV1 kaynak farkını inceleyin.`
    : kdv1Issues.length
    ? kdv1Issues.map((item) => item.message).join(" ")
    : workspace.latest_result?.status === "blocked"
    ? (workspace.latest_result.issues || [])[0]?.message || "Aşağıdaki veri kontrollerini tamamlayın."
    : workspace.latest_result
    ? "Hesap sonucu aşağıda gösteriliyor. Kaynaklar veya kararlar değiştiyse yeniden hesaplayın."
    : blockingWarningCount && reviewCount
    ? `${blockingWarningCount} kapasite uyarısını ve ${reviewCount} fatura satırını kontrol edin.`
    : blockingWarningCount
    ? `${blockingWarningCount} kapasite uyarısını kontrol edin.`
    : reviewCount
    ? `${reviewCount} fatura satırını kontrol edin.`
    : is301 && productionIssues.length
    ? productionIssues[0].action
    : is338 && manufacturerBlockers.length
    ? manufacturerBlockers.map((item) => item.message).join(" ")
    : !workspace.can_calculate
    ? "Kapasiteyle eşleşen doğrudan girdileri kontrol edin."
    : "Tüm zorunlu kaynaklar ve kontroller tamamlandı.";

  function renderLoadingRecipeInputs(question: WorkflowQuestion) {
    if (question.kind !== "loading" || !question.loading_summary?.reasons.some(reason => ["recipe", "recipe_link"].includes(reason.code))) return null;
    const rows = workspace!.invoice_analysis.filter(row => question.invoice_line_ids?.includes(row.invoice_line_id));
    const missing = missingLoadingRecipes(rows, workspace!.capacity_recipes);
    const keys = [...new Set(missing.map(row => row.recipe_key))];
    if (!keys.length) return null;
    const blocked = !canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy;
    function editComponent(row: CapacityWorkspace["capacity_recipes"][number], field: "material_quantity" | "material_unit", value: string) {
      setDecisionRecipeUpdates(current => {
        const previous = current.find(update => update.recipe_key === row.recipe_key);
        const components = previous?.components || workspace!.capacity_recipes.filter(component => component.recipe_key === row.recipe_key)
          .map(component => ({ component_key: component.component_key, material_code: component.material_code,
            material_name: component.material_name, material_unit: component.material_unit, material_quantity: component.material_quantity }));
        return [...current.filter(update => update.recipe_key !== row.recipe_key), {
          recipe_key: row.recipe_key, output_unit: previous?.output_unit || row.output_unit,
          components: components.map(component => component.component_key === row.component_key
            ? { ...component, [field]: field === "material_quantity" ? value.replace(",", ".") : value } : component),
        }];
      });
    }
    return <section aria-label="Eksik ürün başına tüketimi tamamla">
      {keys.map(recipeKey => {
        const components = missing.filter(row => row.recipe_key === recipeKey);
        const product = components[0];
        return <details key={recipeKey} className="smart-inline-recipe">
          <summary>{product.product_name} · Tüketim miktarını gir</summary>
          <div className="smart-recipe-components">{components.map(row => {
            const draft = decisionRecipeUpdates.find(update => update.recipe_key === recipeKey)?.components
              .find(component => component.component_key === row.component_key);
            return <label key={row.component_key}>
              <span><strong>{row.material_name}</strong></span>
              <input aria-label={`${row.product_name} ${row.material_name} tüketim miktarı`} inputMode="decimal"
                disabled={blocked} value={draft?.material_quantity ?? (Number(row.material_quantity) > 0 ? row.material_quantity : "")}
                onChange={event => editComponent(row, "material_quantity", event.target.value)} />
              {row.material_unit ? <small>{row.material_unit} / {row.output_quantity} {row.output_unit}</small>
                : <select aria-label={`${row.product_name} ${row.material_name} tüketim birimi`} disabled={blocked}
                    value={draft?.material_unit || ""} onChange={event => editComponent(row, "material_unit", event.target.value)}>
                  <option value="">Tüketim birimi seçin</option>
                  {[["KGM", "Kilogram"], ["GRM", "Gram"], ["TNE", "Ton"], ["C62", "Adet"], ["LTR", "Litre"], ["MTR", "Metre"], ["MTK", "Metrekare"], ["MTQ", "Metreküp"]]
                    .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>}
            </label>;
          })}</div>
        </details>;
      })}
      {keys.length > 0 && <p>Miktarlar HESAPLA’ya bastığınızda birlikte kaydedilir ve hesaplamada kullanılır.</p>}
    </section>;
  }

  function renderLoadingReview(question: WorkflowQuestion) {
    const action = getLoadingAction(question);
    const blocked = !canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy;
    const scopedUpload = (upload: (files: File[]) => void) => (files: File[]) => {
      setQuestionFeedbackId(question.id);
      upload(files);
    };
    return <LoadingSourceReview key={question.id} question={question} action={action}
      caseId={refundCase.id} rows={workspace!.invoice_analysis.filter(row => question.invoice_line_ids?.includes(row.invoice_line_id))}
      recipes={workspace!.capacity_recipes} recipeSources={workspace!.loading_recipe_checks} onReviewRecipes={() => scrollTo("smart-recipes")} disabled={blocked} onSaved={load} onEditingChange={trackRecipeEditing}
      onReviewLoading={() => void workflowAction("review_loading", { question_id: question.id })}
      onRecheck={() => void workflowAction("answer", { question_id: question.id, value: action === "accounting" ? "updated" : "recheck_sources" })}>
      {action === "accounting" ? <UploadCard number="1" title="Bu faturaların e-Defter / muavin kaynağı"
        detail="Muhasebe kaynakları şirket belgeleri sayfasından yüklenir ve bu dosyada otomatik kullanılır."
        status={uploadProgress.ledger ? "İşleniyor" : "Dosya seçebilirsiniz"} accept=".xml,.xlsx,.xls" multiple allowFolder
        disabled={blocked}><button type="button" className="secondary-button" onClick={onOpenSources}>Şirket belgelerini aç</button></UploadCard> : <>
        {action === "recipe" && <UploadCard number="1" title="Düzeltilmiş reçete Excel’i"
          detail="Gerekliyse mevcut reçete dosyasının düzeltilmiş tamamını yükleyin; yalnız bu karttaki satırları içeren bir kesit yüklemeyin."
          status={workspace!.capacity_recipe_import ? "Mevcut dosya var" : "Dosya seçebilirsiniz"} accept=".xlsx"
          footer={workspace!.capacity_recipe_import?.filename} disabled={blocked} progress={uploadProgress["capacity-recipe"]}
          onFiles={scopedUpload(files => uploadCapacityRecipe(files))} />}
        <UploadCard number={action === "recipe" ? "2" : "1"} title="Dönem başı stok şablonu"
          detail="1.STOK_TEMPLATE.xlsx içindeki dönem başı kalan miktar ve kullanılabilir KDV’yi doldurun."
          status={summary?.current_inventory_movement_count ? "Mevcut hareketler var" : "Dosya seçebilirsiniz"} accept=".xlsx" multiple
          disabled={blocked} progress={uploadProgress.inventory} onFiles={scopedUpload(uploadOpeningStock)} />
        {action === "stock" && (workspace!.opening_stock_import?.automatic ? <UploadCard number="2"
          title="Önceki yıldan devreden stok"
          detail={`${workspace!.opening_stock_import.source_year} yılından kalan stok otomatik alındı. Stok devir Excel’i yüklemeniz gerekmiyor.`}
          status="Otomatik devralındı"
          footer={`${workspace!.opening_stock_import.accepted_count.toLocaleString("tr-TR")} stok satırı`} /> : <UploadCard number="2" title="Dönem başı stok kaynağını düzelt"
          detail="Sorun dönem başı stoktaysa 1.STOK_TEMPLATE dosyasının düzeltilmiş tamamını yükleyin."
          status={workspace!.opening_stock_import ? "Mevcut dosya var" : "Dosya seçebilirsiniz"} accept=".xlsx"
          footer={workspace!.opening_stock_import?.filename} disabled={blocked} progress={uploadProgress.opening} onFiles={scopedUpload(uploadOpeningStock)} />)}
      </>}
    </LoadingSourceReview>;
  }

  function scrollTo(sectionId: string) {
    const target = document.getElementById(sectionId);
    for (let ancestor: HTMLElement | null = target; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor instanceof HTMLDetailsElement && ancestor.id !== "smart-result") ancestor.open = true;
    }
    window.requestAnimationFrame(() => {
      if (!target) return;
      const margin = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
      window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - margin, left: 0, behavior: "smooth" });
    });
  }

  function openPendingQuestion(question: WorkflowQuestion) {
    setFocusQuestion({ id: question.id });
    requestAnimationFrame(() => {
      const recipeQuestion = (question.members || [question]).every(member => member.kind === "recipe_source");
      const ids = [...(recipeQuestion ? ["recipe-sources-combined"] : []), question.id, ...(question.members || []).map(member => member.id)];
      const targetId = ids.flatMap(id => [`smart-decision-${id}`, `smart-question-${id}`])
        .find(id => document.getElementById(id)) || "smart-purchase-matching";
      scrollTo(targetId);
    });
  }

  const recipeChoiceAvailable = is439 && Boolean(workspace.capacity_report && workspace.capacity_recipe_import);
  const openingChoiceAvailable = is439 && Boolean(workspace.opening_stock_source?.manual_import &&
    workspace.opening_stock_source.lists.prior_input_vat && workspace.opening_stock_source.lists.prior_loaded_vat);
  const sourceCheckbox = (source: "recipe" | "opening", value: string, label: string, visible: boolean) => visible ? <label className="smart-source-choice">
    <input type="checkbox" checked={(workspace.source_preferences?.[source] || (source === "opening" ? "manual" : "")) === value}
      disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy}
      onChange={event => void setSourcePreference(source, event.target.checked ? value : "")} />
    <span>{label}</span>
  </label> : null;

  const actionRequiredInCalculation = is439 && (reviewing23 || Boolean(workflow439));
  const displayedActionIssues = actionResultIssues.filter((item) => !["incomplete-source-year-purchase-invoices", "unverified-input-withholding-vat"].includes(item.code) && (item.code !== "sales-reconciliation" || !sourceReconciliation));
  const isReturnInvoiceIssue = (item: (typeof displayedActionIssues)[number]) =>
    is439 && item.code.startsWith("return-") && Number(item.metadata?.return_invoice_id) > 0;
  const returnActionIssues = displayedActionIssues.filter(isReturnInvoiceIssue);
  const otherActionIssues = displayedActionIssues.filter(item => !isReturnInvoiceIssue(item));
  const renderActionIssue = (item: (typeof displayedActionIssues)[number]) => {
    const returnKdv1Issue = /^return-kdv1-additional-vat(?:-(?:short|missing):\d{6})?$/.test(item.code);
    const relatedInvoiceIds = returnKdv1Issue
      ? item.metadata?.return_invoice_ids ?? item.metadata?.invoice_ids
      : item.metadata?.invoice_ids;
    const invoiceIds = Array.isArray(relatedInvoiceIds) ? relatedInvoiceIds.map(Number).filter(Boolean) : [];
    return <div className={`issue ${item.severity}`} key={item.code}>
      <strong>{formatMoneyText(item.message)}</strong>
      {item.code.startsWith("return-") && Number(item.metadata?.return_invoice_id) > 0 && <>
        <p>İade faturası: <strong>{String(item.metadata?.return_invoice_no || "")}</strong> · Referans: {String(item.metadata?.original_invoice_nos || item.metadata?.original_invoice_no || "belirtilmemiş")}</p>
        <div className="smart-guided-actions">
          <button className="secondary-button compact" type="button" onClick={() => onReviewInvoices({ refundCaseId: refundCase.id, invoiceIds: [Number(item.metadata?.return_invoice_id)], code: "unresolved-return", title: "İade faturasını inceleyin", instruction: item.message })}>İade faturasını aç</button>
          <button className="secondary-button compact danger" disabled={!canPrepare || Boolean(busy)} type="button" onClick={() => void setReturnExclusion(Number(item.metadata?.return_invoice_id), String(item.metadata?.return_invoice_no || "İade faturası"), true)}>Yalnız bu dosyada kapsam dışı bırak</button>
        </div>
      </>}
      {item.code === "unverified-recipe-units" && <>
        <details><summary>Tüketim birimi doğrulanamayan hammaddeler</summary><ul>{(Array.isArray(item.metadata?.materials) ? item.metadata.materials : []).map((material, index) => {
          if (typeof material === "string") return <li key={material}>{material.replaceAll("_", " ")}</li>;
          const row = material as { material_name?: string; material_code?: string; sheet?: string; row?: number; message?: string };
          return <li key={index}>{row.material_name || row.material_code || formatMoneyText(row.message)}{row.sheet && ` · ${row.sheet}`}{row.row && `, satır ${row.row}`}</li>;
        })}</ul></details>
        <small>Emin olmadığınız birimleri tahmin ederek girmeniz gerekmiyor. Alış birimi tek başına reçetenin tüketim birimini kanıtlamaz; bu tutarlar doğrulanmış iade olarak gösterilmez.</small>
      </>}
      {item.code === "invoice-download-errors" && <>
        <small>Önce “Saklanan XML’leri yeniden doğrula” düğmesine basın. Sistem entegratörden tekrar indirmeden mevcut belgeleri yeni kurallarla işler.</small>
        <div className="smart-guided-actions">
          <button className="primary-button compact" disabled={!canPrepare || Boolean(busy)} type="button" onClick={() => void calculate("final")}>{busy === "calculate-final" ? "Doğrulanıyor…" : "Saklanan XML’leri yeniden doğrula"}</button>
          {invoiceIds.length > 0 && <button className="secondary-button compact" type="button" onClick={() => onReviewInvoices({ refundCaseId: refundCase.id, invoiceIds, code: item.code, title: "Doğrulanamayan faturalar", instruction: item.message })}>Faturaları göster ({invoiceIds.length})</button>}
        </div>
      </>}
      {returnKdv1Issue && <>
        <small>İlgili dönem PDF’lerini tek tek aramak zorunda değilsiniz. KDV1 kartındaki “Klasör seç” ile hepsini birlikte yeniden yükleyebilirsiniz.</small>
        <div className="smart-guided-actions">
          <button className="primary-button compact" disabled={!canPrepare || Boolean(busy)} type="button" onClick={() => scrollTo("smart-source-uploads")}>KDV1 belgelerini yükle</button>
          {invoiceIds.length > 0 && <button className="secondary-button compact" type="button" onClick={() => onReviewInvoices({ refundCaseId: refundCase.id, invoiceIds, code: item.code, title: "KDV1 düzeltmesi bekleyen alış iadeleri", instruction: item.message })}>İade faturalarını göster ({invoiceIds.length})</button>}
        </div>
      </>}
    </div>;
  };
  const actionRequiredPanel = showActionRequired && <section className={actionRequiredInCalculation ? "smart-action-required" : "panel smart-action-required"} id="smart-action-required">
    <div className="panel-head">
      {actionRequiredInCalculation ? <h3>Açık kontroller</h3> : <div><p className="eyebrow">EYLEM GEREKLİ</p><h2>Hesaplamaya devam etmek için tamamlanması gerekenler</h2></div>}
      <em className="pill partial">{(visibleExceptions.length ? 1 : 0) + unresolvedKdv1SourceCount + hardCapacityIssues.length + actionResultIssues.filter((item) => !["incomplete-source-year-purchase-invoices", "unverified-input-withholding-vat"].includes(item.code)).length} kontrol</em>
    </div>
    {is439 && unresolvedReturnIds.length > 0 && <div className="issue error">
      <strong>{unresolvedReturnIds.length} iade faturasının eşleştirmesi doğrulanamadı</strong>
      <p>Bu faturaları yalnız bu iade dosyasında topluca kapsam dışında bırakabilirsiniz. Seçim ilgili faturaların bütün kalemlerini kapsar; iade tutarı etkilenebilir. Faturalar silinmez.</p>
      <button className="secondary-button danger" type="button"
        disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || pendingReturnExclusions.length === 0}
        onClick={() => {
          setReturnExclusionDrafts(current => ({ ...current,
            ...Object.fromEntries(unresolvedReturnIds.map(id => [id, true])) }));
          setMessage(`${unresolvedReturnIds.length} iade faturası için toplu kapsam dışı seçildi. HESAPLA ile birlikte kaydedilip uygulanacak.`);
        }}>{pendingReturnExclusions.length ? "Eşleşmeyen iadeleri topluca kapsam dışı bırak" : "Toplu kapsam dışı seçildi"}</button>
      <p>Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>
    </div>}
    {hardCapacityIssues.map((item, index) => <div className="issue error" key={`${item.code}-${index}`}><strong>{formatMoneyText(item.message)}</strong><small>Fatura, ürün eşleştirmesi veya üretim/stok kaydındaki miktarı kontrol edin.</small></div>)}
    {otherActionIssues.map(renderActionIssue)}
    {returnActionIssues.length > 0 && <details className="smart-collapsible">
      <summary className="panel-head">
        <strong>İade faturası detayları</strong>
        <span className="smart-collapse-meta"><i aria-hidden="true">⌄</i></span>
      </summary>
      <div className="smart-collapsible-content smart-action-required">
        {returnActionIssues.map(renderActionIssue)}
      </div>
    </details>}
  </section>;

  return (
    <div className="smart-refund-workspace">
      {refundCase.fortis_fast && <section className="panel notice" aria-label={`${refundCase.title} testi`}>
        <div className="panel-head"><div><h2>{refundCase.title}</h2>
          <p>Her ayın ilk {refundCase.fortis_fast.sample_days} günü · normal 12 aylık test akışı</p></div>
          {refundCase.fortis_fast.restartable && <button type="button" className="secondary-button" disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy}
            onClick={() => void run("restart-test", async () => {
              await api(`/api/refund-cases/${refundCase.id}/439-workflow`, {
                method: "POST", body: JSON.stringify({ action: "restart_test" })
              });
              setPurchaseDrafts({}); setAutomaticJob(null); setMissingRecipe(null);
              setSelectedOpeningRecords([]); setEditingRecipeQuestions([]);
            }, "Test başlangıca döndü. Hazır girdiler korundu; önce 2 ve 3 No.lu yüklenimleri hazırlayın.")}>Testi yeniden başlat</button>}
        </div>
        {refundCase.fortis_fast.restartable && <p>Testi yeniden başlat, hazır girdileri koruyup kararları ve sonuçları başlangıca döndürür.
          Önce 2 No genel gider ve 3 No ATİK, ardından 1 No doğrudan yüklenim adımlarını tekrar deneyebilirsiniz.</p>}
        {refundCase.title === "Fortis Fast" && <button type="button" className="secondary-button" disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy} onClick={() => void run("create-faster", async () => {
          await api(`/api/refund-cases/${refundCase.id}/439-workflow`, { method: "POST", body: JSON.stringify({ action: "create_faster_test" }) });
          onBack();
        }, "Fortis Faster oluşturuldu. İade dosyaları listesinden açabilirsiniz.")}>İlk 5 günle Fortis Faster oluştur</button>}
      </section>}
      {Boolean(busy) && <RefundBusyOverlay label="İşlem tamamlanıyor…" />}
      {refreshingResult && <p className="notice" role="status">Hesaplama tamamlandı; sonuçlar ekrana yükleniyor…</p>}
      <button className="text-button back-button" onClick={onBack}>← KDV iade dosyaları</button>
      <DebugTransferControls scope="case" targetId={refundCase.id} disabled={Boolean(busy) || anyUploadBusy || automaticPreparationBusy} />

      <section className="smart-refund-hero">
        <div>
          <p className="eyebrow">KDV İADESİ · OTOMATİK HAZIRLIK</p>
          <h2>{refundCase.taxpayer_title}</h2>
          <p>{refundCase.period_start.slice(0, 7)} – {refundCase.period_end.slice(0, 7)} · {refundCase.transaction_code} {refundCase.transaction_name}</p>
        </div>
        <div className="smart-progress">
          <strong>%{progress}</strong>
          <span><i style={{ width: `${progress}%` }} /></span>
          <small>{progress === 100 ? "Temel kaynaklar hazır" : "Eksik belgeleri yükleyin"}</small>
        </div>
      </section>

      {canApprove && <div className="notice">İnceleme modu: kaynaklar değiştirilemez.</div>}
      {message && <div className="notice" role="status">{message}</div>}
      {missingBomUnits && <section className="panel">
        <h3>Reçetedeki tüketim birimlerini tamamlayın</h3>
        <p>{missingBomUnits.file.name} içindeki bu hammaddelerin birimi mevcut kaynaklardan doğrulanamadı.</p>
        {missingBomUnits.items.map(item => <label key={item.key}>{item.key} · {item.name}
          <select value={item.unit} disabled={anyUploadBusy} onChange={event => setMissingBomUnits(current => current ? { ...current, items: current.items.map(row => row.key === item.key ? { ...row, unit: event.target.value } : row) } : null)}>
            <option value="">Birim seçin</option>{[["C62", "Adet"], ["KGM", "Kilogram"], ["GRM", "Gram"], ["TNE", "Ton"], ["LTR", "Litre"], ["MTR", "Metre"], ["MTK", "Metrekare"], ["MTQ", "Metreküp"], ["SET", "Set"], ["PR", "Çift"]].map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </label>)}
        <button className="primary-button" disabled={!canPrepare || anyUploadBusy || missingBomUnits.items.some(item => !item.unit)} onClick={() => uploadBom([missingBomUnits.file], Object.fromEntries(missingBomUnits.items.map(item => [item.key, item.unit])))}>Birimleri kaydet ve reçeteyi aktar</button>
      </section>}
      {error && <div className="banner-error refund-error-banner" ref={errorBannerRef} role="alert" tabIndex={-1}>
        <div><p>{openingError(error)}</p>{openingError(error) !== error && <button type="button" className="secondary-button compact" onClick={() => scrollTo("smart-opening-stock-issues")}>Kontrol kayıtlarını göster</button>}{errorContext?.invoice_id && !errorContext.question_id && <button type="button" className="refund-error-invoice-link" onClick={() => onReviewInvoices({
          refundCaseId: refundCase.id, invoiceIds: [errorContext.invoice_id!], code: errorContext.code,
          title: `${errorContext.invoice_no} · ${errorContext.item_name}`, instruction: errorContext.instruction
        })}>İlgili faturayı incele →</button>}{errorContext?.question_id && <button type="button" className="refund-error-invoice-link" onClick={() => {
          const rowId = `smart-question-${errorContext.member_id}`;
          const groupId = `smart-question-${errorContext.question_id}`;
          document.getElementById(groupId)?.querySelector<HTMLButtonElement>("[data-edit-answer]")?.click();
          window.requestAnimationFrame(() => {
            const invoiceRowId = `smart-invoice-line-${errorContext.invoice_line_id}`;
            const targetId = [invoiceRowId, rowId, groupId].find(id => document.getElementById(id)) || "smart-439-questions";
            scrollTo(targetId);
            document.getElementById(targetId)?.querySelector<HTMLElement>("input:not(:disabled), select:not(:disabled)")?.focus({ preventScroll: true });
          });
        }}>İlgili cevabı düzelt →</button>}</div>
        <button type="button" aria-label="Hata mesajını kapat" onClick={() => setError("")}>×</button>
      </div>}
      {Object.keys(uploadProgress).length > 0 && <div className="notice" role="status">Kaynak yüklemeleri sürüyor. Dosyadan çıkıp geri dönebilirsiniz; bu sekmeyi kapatmayın veya sayfayı yenilemeyin. Eksik ay ve mutabakat kontrolleri yüklemeler bitince güncellenecek.</div>}
      {is338 && workspace.semantic_readiness && <section className="panel semantic-readiness-panel">
        <div className="panel-head"><div><p className="eyebrow">SEMANTIC YETERLİLİK</p><h2>{workspace.semantic_readiness.status} · %{workspace.semantic_readiness.progress}</h2></div></div>
        {workspace.semantic_readiness.missing_semantics.length > 0
          ? <div className="table-list">{workspace.semantic_readiness.missing_semantics.map((item) => <div className="table-row" key={item.key}><span><strong>{item.label}</strong><small>{formatMoneyText(item.reason)}</small></span><span><small>Olası kaynaklar</small><strong>{item.possible_sources.join(" · ")}</strong></span></div>)}</div>
          : <p className="muted">Zorunlu semantic gerçekler elde edildi; kaynak mutabakatı ve 301 hesap kontrolleri ayrıca uygulanır.</p>}
      </section>}

      {is338 && <section className="panel">
        <div className="panel-head"><div><p className="eyebrow">RESMÎ BELGE KİMLİĞİ</p><h2>Kapasite/imalatçı/oda belgesi bilgileri</h2></div></div>
        <div className="legislation-meta">
          <label>Belge numarası<input value={legalIdentity.document_number} onChange={(event) => setLegalIdentity({ ...legalIdentity, document_number: event.target.value })} /></label>
          <label>Belge tarihi<input type="date" value={legalIdentity.document_date} onChange={(event) => setLegalIdentity({ ...legalIdentity, document_date: event.target.value })} /></label>
          <label>Düzenleyen kurum<input value={legalIdentity.issuing_authority} onChange={(event) => setLegalIdentity({ ...legalIdentity, issuing_authority: event.target.value })} /></label>
          <button className="primary-button compact document-identity-save" disabled={!canPrepare || Boolean(busy) || !Object.values(legalIdentity).every((value) => value.trim())} onClick={saveLegalIdentity}>{busy === "legal-identity" ? "Kaydediliyor…" : "Kaydet"}</button>
        </div>
        <small>Bu bilgiler iade dosyasına kaydedilir ve resmî dayanak yüklenirken kullanılır.</small>
      </section>}

      {is439 && <section className="panel">
        <h2>Faaliyet türü</h2>
        <label>439 faaliyet türü<select aria-label="439 faaliyet türü" value={workflow439?.production_requirement.mode || "production"}
          disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy}
          onChange={event => void workflowAction("business_mode", { value: event.target.value })}>
          <option value="production">Üretim yapıyoruz; al-sat da yapabiliriz</option>
          <option value="trade">Yalnız al-sat yapıyoruz</option>
        </select></label>
        <p>{tradeOnly ? "Kapasite raporu opsiyoneldir; reçete istenmez. Satılan mallar alış ve stok miktarlarıyla eşleştirilir. İlgili hizmet ve genel gider KDV’sinden uygun pay hesaplanır."
          : "Üretim için kapasite raporu veya reçete Excel’i yükleyin. Üretilen ürünlerde reçete tüketimi, aynen alınıp satılan ürünlerde alış–satış eşleştirmesi kullanılır."}</p>
      </section>}

      {is301 && <div className="notice">GÇB ve ETGB belgelerini GGB/GÇB menüsünden yükleyip doğrulayın; “Doğrulanmış belgeleri dosyada kullan” ile bu iade dosyasına aktarın.</div>}
      <div className="notice">Ortak kaynaklar şirket belgeleri sayfasında yüklenir. <button type="button" className="text-button" onClick={onOpenSources}>Şirket belgelerini aç</button></div>
      <section className="smart-upload-grid" id="smart-source-uploads" onFocusCapture={() => setQuestionFeedbackId(null)} onPointerDownCapture={() => setQuestionFeedbackId(null)}>
        <UploadCard
          number="1"
          title={is439 ? "Faturalar (zorunlu)" : "Faturalar"}
          detail="Faturalar ayrı Faturalar sayfasından entegratörle çekilir veya dosyadan yüklenir."
          status={invoicePresentation.status}
          footer={`${(summary?.taxpayer_invoice_count || 0).toLocaleString("tr-TR")} kayıtlı · ${((summary?.incoming_invoice_count || 0) + (summary?.outgoing_invoice_count || 0)).toLocaleString("tr-TR")} dosya döneminde${invoicePresentation.message ? ` · ${invoicePresentation.message}` : ""}`}
        />
        {is338 && <UploadCard
          number="2"
          title="GİB GÇB / ETGB listesi"
          detail="Şirketin doğrulanmış GÇB/ETGB belgeleri dönem ve iade türüne göre otomatik kullanılır."
          status={summary?.export_customs_count ? "Hazır" : "Bekleniyor"}
          accept=".xlsx"
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress["gib-customs"])}
          progress={uploadProgress["gib-customs"]}
          footer={summary?.export_customs_count ? `${summary.export_customs_count} beyanname` : undefined}
        />}
        {is338 && <UploadCard
          number="3"
          title="GİB 338 satış faturası listesi"
          detail="Şirketin uygun GÇB/ETGB kalemleri satış faturalarıyla otomatik eşleştirilir."
          status={summary?.export_sales_line_count ? "Hazır" : "Bekleniyor"}
          accept=".xlsx"
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress["gib-export-sales"])}
          progress={uploadProgress["gib-export-sales"]}
          footer={summary?.export_sales_line_count ? `${summary.export_sales_line_count} GTİP satırı` : undefined}
        />}
        <UploadCard
          number={is338 ? "3" : "2"}
          title={is439 ? "Beyannameler (zorunlu)" : "Beyannameler"}
          detail="Şirket belgelerindeki KDV1 beyannameleri ilgili dönemlerde otomatik kullanılır."
          status={summary?.declarations_ready ? "Hazır" : "Bekleniyor"}
          accept=".pdf,.xml,.json,.xls,.xlsx,.csv,application/vnd.ms-excel"
          multiple
          allowFolder
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress.declaration)}
          progress={uploadProgress.declaration}
          footer={summary?.required_declaration_count ? `${summary.declaration_count} / ${summary.required_declaration_count} dönem${summary.missing_declaration_periods.length ? ` · eksik ${summary.missing_declaration_periods.join(", ")}` : ""}${summary.official_loaded_vat_count ? ` · ${summary.official_loaded_vat_count} yüklenim satırı` : ""}` : undefined}
        />
        <UploadCard
          number={is338 ? "4" : "3"}
          title="e-Defter / muavin"
          detail="Şirketin ilgili dönemdeki e-Defter / muavin kayıtları otomatik kullanılır."
          status={summary?.ledger_ready ? "Hazır" : "Bekleniyor"}
          accept=".xml,.xlsx,.xls"
          multiple
          allowFolder
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress.ledger)}
          progress={uploadProgress.ledger}
          footer={summary?.required_ledger_period_count ? `${summary.ledger_period_count} / ${summary.required_ledger_period_count} dönem${summary.missing_ledger_periods.length ? ` · eksik ${summary.missing_ledger_periods.join(", ")}` : ""}` : summary?.ledger_row_count ? `${summary.ledger_row_count.toLocaleString("tr-TR")} satır` : undefined}
        />
        <UploadCard
          number={requiresExportChain ? (is338 ? "8" : "6") : "4"}
          title="Kapasite raporu (opsiyonel)"
          detail="Şirket belgelerindeki geçerli kapasite raporları bu dosyada otomatik kullanılır."
          status={workspace.capacity_report ? "Hazır" : "Opsiyonel"}
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          footer={workspace.capacity_report ? `${workspace.capacity_report.analysis.report_count || 1} aktif rapor` : undefined}
        />
        {(is439 ? ["prior_input_vat", "prior_loaded_vat"] as const : ["prior_input_vat"] as const).map((kind, index) => {
          const source = workspace.opening_stock_source?.lists[kind];
          const ignored = workspace.opening_stock_source?.source === "manual";
          return <UploadCard key={kind} number={String(5 + index)}
            title={`Önceki dönem ${index === 0 ? "indirilecek" : "yüklenilen"} KDV listesi (opsiyonel)`}
            detail={`${refundCase.period_start} öncesindeki dönemlerin listesini yükleyin. İndirilecek listesinde fatura tarihleri en fazla 24 ay önceye ait olabilir.`}
            status={ignored ? "Stok Excel’i esas alınıyor" : source ? "Hazır" : "Opsiyonel"}
            accept=".xls,.xlsx,.csv" multiple={kind === "prior_input_vat"} disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress[kind])}
            progress={uploadProgress[kind]} footer={source ? `${source.filename} · ${source.row_count.toLocaleString("tr-TR")} satır` : undefined}
            onFiles={(files) => uploadPriorVat(kind, files)}
            selection={sourceCheckbox("opening", "prior_vat_lists", "İndirilecek ve yüklenilen Excel’lerini kullan, dönem başı stok Excel’ini kullanma", openingChoiceAvailable)} />;
        })}
        {(is301 || is439) && <UploadCard
          number={is439 ? "7" : "4"}
          title="Dönem başı stok Excel’i (opsiyonel)"
          detail={is439
            ? "Her veri satırında stok kodu zorunludur; kodu eksik tek satır varsa dosya kabul edilmez."
            : "Dönem başında elde kalan miktarları, alış faturalarını ve kalan KDV’yi yükleyin."}
          status={workspace.opening_stock_import ? workspace.opening_stock_import.prior_vat_lists ? "KDV listelerinden hesaplandı" : "Hazır" : "Opsiyonel"}
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress.opening)}
          progress={uploadProgress.opening}
          footer={workspace.opening_stock_import ? `${workspace.opening_stock_import.filename} · ${workspace.opening_stock_import.accepted_count.toLocaleString("tr-TR")} stok satırı` : undefined}
          onFiles={uploadOpeningStock}
          selection={sourceCheckbox("opening", "manual", "Bunu kullan, indirilecek ve yüklenilen Excel’lerini kullanma", openingChoiceAvailable)}
        />}

        {is301 && Boolean(summary?.inventory_movement_count) && productionIssues.some((item) => item.code === "missing-direct-stock-trace") && <UploadCard
          number="7"
          title="Reçete / BOM gerekli"
          detail="Yüklenen ERP hareketleri mamul–girdi ilişkisini kuramadı. Yalnız bu nedenle reçete verisi gerekiyor."
          status="Eksik ilişki"
          accept=".xlsx"
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress.bom)}
          progress={uploadProgress.bom}
          onFiles={uploadBom}
        />}
        {is338 && <>
          <article className="smart-upload-card">
            <span className="smart-upload-number">6</span>
            <div>
              <strong>İmalatçı belgesi ve üretim teyidi</strong>
              <small>Geçerli imalatçı belgesini yükleyin; işçi/araç parkı veya üretim altyapısı şartını ve ihraç malının belge kapsamındaki üretim ilişkisini hangi kayda göre doğruladığınızı açıklayın.</small>
              <input
                aria-label="Üretim ilişkisi açıklaması"
                placeholder="Üretim ilişkisini kısaca açıklayın"
                value={manufacturerExplanation}
                onChange={(event) => setManufacturerExplanation(event.target.value)}
              />
            </div>
            <em>{manufacturerBlockers.some((item) => item.code === "missing-document:manufacturer_certificate") ? "Bekleniyor" : "Hazır"}</em>
            <label aria-disabled={!canPrepare || manufacturerExplanation.trim().length < 3} className="smart-file-button">
              Dosya seç
              <input
                accept=".pdf,.xlsx,.xml"
                disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress.manufacturer) || manufacturerExplanation.trim().length < 3}
                type="file"
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  event.target.value = "";
                  if (files.length) upload338Document("manufacturer_certificate", files);
                }}
              />
            </label>
          </article>
          <UploadCard
            number="7"
            title="Meslek odası üyelik belgesi"
            detail="İmalatçı vasfını destekleyen ilgili meslek odası üyelik belgesini yükleyin."
            status={manufacturerBlockers.some((item) => item.code === "missing-document:chamber_membership") ? "Bekleniyor" : "Hazır"}
            accept=".pdf,.xlsx,.xml"
            disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress.chamber)}
            progress={uploadProgress.chamber}
            onFiles={(files) => upload338Document("chamber_membership", files)}
          />
        </>}
        {is439 && !tradeOnly && <UploadCard
          id="smart-company-recipe-upload"
          number="8"
          title="Reçete Excel’i (opsiyonel)"
          detail="Yalnız 2.RECETE_TEMPLATE.xlsx şablonunu doldurup yükleyin."
          status={workspace.capacity_recipe_import?.pending_quantity_recipe_count ? "Miktar bekliyor" : workspace.capacity_recipe_import?.empty_products?.length ? "Eksik reçeteler" : workspace.capacity_recipe_import ? "Hazır" : "Opsiyonel"}
          accept=".xlsx"
          disabled={!canPrepare || Boolean(busy) || Boolean(uploadProgress["capacity-recipe"])}
          progress={uploadProgress["capacity-recipe"]}
          footer={workspace.capacity_recipe_import ? `${workspace.capacity_recipe_import.filename} · ${workspace.capacity_recipe_import.imported_recipe_count.toLocaleString("tr-TR")} ürün reçetesi${workspace.capacity_recipe_import.canonical_confirmed_recipe_count !== undefined ? ` · ana hesapta ${workspace.capacity_recipe_import.canonical_confirmed_recipe_count.toLocaleString("tr-TR")} onaylı ürün reçetesi` : ""}${workspace.capacity_recipe_import.automatic_material_mapping_count ? ` · ${workspace.capacity_recipe_import.automatic_material_mapping_count.toLocaleString("tr-TR")} güvenli hammadde eşleşmesi` : ""}${workspace.capacity_recipe_import.company_only_material_row_count ? ` · ${workspace.capacity_recipe_import.company_only_material_row_count.toLocaleString("tr-TR")} satır yalnız şirket reçetesi kanıtıyla korundu` : workspace.capacity_recipe_import.skipped_material_row_count ? ` · ${workspace.capacity_recipe_import.skipped_material_row_count.toLocaleString("tr-TR")} eski aktarım satırı için dosyayı yeniden yükleyin` : ""}` : undefined}
          onFiles={(files) => uploadCapacityRecipe(files)}
          selection={sourceCheckbox("recipe", "excel", "Bunu kullan, kapasite raporunu kullanma", recipeChoiceAvailable)}
        >
          {workspace.capacity_recipe_import?.skipped_products.length ? <aside className="smart-recipe-capacity-note" aria-label="Kapasite raporu eşleşmesi">
            <strong>Kapasite raporu eşleşmesi doğrulanamadı</strong>
            <span>{workspace.capacity_recipe_import.skipped_products.join(", ")}</span>
            <p>Bu ürünlerin kapasite raporuyla bağlantısı kurulamadı. Reçete Excel’indeki bilgiler hesaplama için ayrıca kontrol edilir; bu uyarı tek başına reçetenin hesap dışında kaldığı anlamına gelmez.</p>
          </aside> : null}
          {Boolean(workspace.capacity_recipe_import?.unmatched_products?.length || workspace.capacity_recipe_import?.ambiguous_products?.length) && <small className="issue warning">Ana hesapta mamulle eşleşmediği için aktarılamayan reçeteler: {[...(workspace.capacity_recipe_import?.unmatched_products || []), ...(workspace.capacity_recipe_import?.ambiguous_products || [])].map((item) => item.code || item.name).join(", ")}</small>}
        </UploadCard>}

      </section>

      {is439 && Boolean(workspace.opening_stock_source?.issues.length || excludedOpeningItems.length) && <section className="panel smart-opening-stock-review" aria-label="Açılış stok kontrolü">
        <h2>{workspace.opening_stock_source?.ready ? "Açılış stoku hazır" : "Açılış stok eşleştirmesi tamamlanmalı"}</h2>
        <p>{workspace.opening_stock_source?.ready ? "Açılış stoku kapsam dışında bırakılan kayıtlar hariç hesaplandı." : openingSourcesUploaded ? `İki KDV listesi okundu. ${workspace.opening_stock_source!.issues.length} kontrol bekliyor. 1 No.lu hesaplama için bu kayıtları tamamlayın veya kapsam dışında bırakın.` : "Dönem başı stok Excel’i veya önceki dönemin iki KDV listesini yükleyin."}</p>
        {Boolean(workspace.opening_stock_source?.ignored_non_stock_count) && <p>{workspace.opening_stock_source!.ignored_non_stock_count} hizmet/genel gider satırı açılış hammadde hesabına alınmadı.</p>}
        {Boolean(workspace.opening_stock_source?.issues.length) && <details id="smart-opening-stock-issues"><summary>{workspace.opening_stock_source!.issues.length} kontrol kaydını göster</summary>
          {openingReviewItems.length > 0 ? <>
            <p>Seçtiğiniz kayıtlar açılış stok hesabından çıkarılır; bu kayıtlardan devreden miktar ve KDV hesaba alınmaz. GGB numarası bulunan ithalat kayıtları ayrı değerlendirilir. Kararı aşağıdan geri alabilirsiniz.</p>
            <label><input type="checkbox" disabled={openingScopeBusy} checked={selectedOpeningIds.length === openingReviewItems.length} onChange={event => setSelectedOpeningRecords(event.target.checked ? openingReviewItems.map(item => item.id) : [])} /> Tüm kontrol kayıtlarını kapsam dışında bırak ({openingReviewItems.length})</label>
            <ul>{openingReviewItems.map(item => <li key={item.id}><label><input type="checkbox" disabled={openingScopeBusy} checked={selectedOpeningIds.includes(item.id)} onChange={event => setSelectedOpeningRecords(current => event.target.checked ? [...current, item.id] : current.filter(id => id !== item.id))} /> {formatMoneyText(item.message)}</label></li>)}</ul>
            <p>{selectedOpeningIds.length} kayıt kapsam dışında bırakılmak üzere seçili. Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>
          </> : <ul>{workspace.opening_stock_source!.issues.map((issue, index) => <li key={index}>{issue}</li>)}</ul>}
        </details>}
        {excludedOpeningItems.length > 0 && <details><summary>Kapsam dışında bırakılan kayıtlar ({excludedOpeningItems.length})</summary>
          <ul>{excludedOpeningItems.map(item => <li key={item.id}>{formatMoneyText(item.message)} <button type="button" className="text-button" disabled={openingScopeBusy} onClick={() => setOpeningExclusions([item.id], false)}>{openingRestores.includes(item.id) ? "HESAPLA ile kapsama alınacak" : "Kapsama geri al"}</button></li>)}</ul>
        </details>}
      </section>}

      {is439 && (!sectorSaved || !sectorReady) && <section className="panel" id="smart-439-sector">
        <h2>GİB sektör seçimi</h2>
        <label>İadeye konu faaliyet<select aria-label="İadeye konu faaliyet" value={[sectorCode, subsectorCode].filter(Boolean).join("/")} disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || refreshingResult || anyUploadBusy} onChange={event => {
          sectorDraftEdited.current = true;
          const [sector, subsector = ""] = event.target.value.split("/");
          setSectorCode(sector); setSubsectorCode(subsector);
        }}>
          <option value="">Faaliyeti listeden seçin</option>
          {(workflow439?.sector_options || []).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select></label>
        <p>Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>
      </section>}
      {is439 && workflow439?.loading_review?.prepared && <LoadingReview439 key={`${refundCase.id}:${selectionSavedVersion}`} onDraftsChange={setLoadingDraftRows} caseId={refundCase.id} review={{ ...workflow439.loading_review, period_start: refundCase.period_start }} disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy} onChange={async review => {
        if (review) setWorkflow439(current => current ? { ...current, loading_review: review } : current);
        await load();
      }} />}
      {reviewing23 && <section className="panel"><h2>{workflow439?.loading_review?.prepared ? "2 ve 3 No.lu kaynakları yeniden kontrol et" : "Önce 2 ve 3 No.lu yüklenimleri hazırla"}</h2>
        <p>Yüklenen kaynaklarla önce genel gider ve ATİK listeleri hazırlanır. Açılış stok eşleştirmeleri 1 No.lu yüklenim ve nihai iade hesabından önce tamamlanmalıdır. Reçete Excel’i isteğe bağlıdır.</p>
        {!required439SourcesReady && <p className="issue warning" role="status">HESAPLA için faturalar, beyannameler, e-Defter / muavin, üretim reçetesi ve açılış stok kaynaklarını tamamlayın.</p>}
        {loadingReviewPending && <p id="loading-review-calculation-hint" role="status">Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır. 2 ve 3 No.lu listelerin kontrolleri de aynı işlemde yapılır.</p>}
        {loadingReviewNeedsRefresh && <p>Kaynak kayıtlar değiştiği için listeler yenilenmeli. HESAPLA ile güncel kaynaklar kullanılır.</p>}
        {automaticPreparationBusy && <div className="smart-job-progress" role="status">
          <strong>{automaticJob.status === "queued" ? "Liste hazırlama sırası bekleniyor" : String(automaticJob.result?.stage || "Listeler hazırlanıyor")}</strong>
          <progress max={100} value={automaticJob.progress} aria-label="Yüklenim listelerini hazırlama ilerlemesi" />
          <span>%{automaticJob.progress} · İşlem sürüyor; listeler hazır olduğunda sayfa kendiliğinden güncellenecek.</span>
        </div>}
        {interruptedCalculation && <p className="issue warning" role="alert">Hesaplama kesintiye uğradı. Kaydedilmiş cevaplarınız korundu. Yeniden deneyebilirsiniz.</p>}
        {actionRequiredPanel}
        <button type="button" className="primary-button" aria-describedby={loadingReviewPending ? "loading-review-calculation-hint" : undefined} disabled={!canPrepare || !calculationReady || Boolean(busy) || anyUploadBusy} onClick={() => void calculate("final")}>{automaticPreparationBusy ? `${String(automaticJob.result?.stage || "Listeler hazırlanıyor")} · %${automaticJob.progress}` : interruptedCalculation ? "Hesaplamayı yeniden dene" : loadingReviewNeedsRefresh ? "Kaynakları yenile" : "HESAPLA"}</button>
      </section>}
      {is439 && !reviewing23 && workflow439 && <section className="panel smart-action-required" id="smart-439-questions" style={{ scrollMarginTop: 0 }} aria-live="polite">
        <div inert={automaticPreparationBusy || refreshingResult}>
        {workflow439.material_review?.required && <div className="notice warning" role="status">
          <h3>Önceki 1 No.lu kararları topluca değerlendirin</h3>
          <p>{workflow439.material_review.trade_count > 0 && <>{workflow439.material_review.trade_count} eski al-sat seçimi hammadde gruplarıyla yeniden incelenecek. </>}{workflow439.material_review.excluded_count} fatura satırında eski kapsam dışı kararı var. Bu kararların yeni hesapta korunmasını veya yeniden değerlendirilmesini seçin.</p>
          <p>2 ve 3 No.lu listeleriniz korunur. Aşağıdaki seçiminiz yalnız 1 No.lu kayıtlar içindir.</p>
          <button type="button" className="primary-button" disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy} onClick={() => void workflowAction("review_type1", {keep_exclusions: false})}>Kapsam dışı kararları da yeniden değerlendir</button>
          <button type="button" className="secondary-button" disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy} onClick={() => void workflowAction("review_type1", {keep_exclusions: true})}>Kapsam dışı kararlarımı koru; hammadde eşleşmelerini yenile</button>
        </div>}
        <div className="panel-head"><div><p className="eyebrow">439 · 1 NO.LU DOĞRUDAN YÜKLENİM</p><h2>{workflow439.material_review?.required ? "Önceki kararların nasıl kullanılacağını seçin" : !required439SourcesReady ? "Hesaplama için kaynakları tamamlayın" : workflow439.needs_choice ? "Nasıl devam etmek istersiniz?" : current439Check ? (workflow439.questions.length ? "Kontrol tamamlandı; aşağıdaki eksikler devam ediyor" : "Kontrol tamamlandı") : workflow439.questions.length ? "Hazır kayıtları hesaplayın; açık eşleştirmeleri tamamlayın" : workflow439.limited_scope ? "Seçtiğiniz kapsamla hesap" : workspace.latest_result?.status === "completed" ? "Hesap hazır" : "Belgeleri yükleyin ve HESAPLA’ya basın"}</h2></div></div>
        {!required439SourcesReady && <div id="smart-439-source-blocker" className="issue warning" role="status">
          <p>HESAPLA kapalı: {missingSources.join(", ")} tamamlayın.</p>
          {!workspace.opening_stock_source?.ready && <>
            <p>{openingSourcesUploaded
              ? `Önceki dönem KDV listeleri okundu; açılış stoku ${workspace.opening_stock_source?.issues.length || 0} kontrol kaydı nedeniyle henüz hesaplanamadı. Listedeki eksikleri tamamlayın, ilgili kayıtları kapsam dışında bırakın veya stok kodları eksiksiz dönem başı stok Excel’ini yükleyin.`
              : "Dönem başı stok Excel’i veya önceki dönemin indirilecek ve yüklenilen KDV listelerini birlikte yükleyin."}</p>
            {Boolean(workspace.opening_stock_source?.issues.length) && <button type="button" className="secondary-button compact" onClick={() => scrollTo("smart-opening-stock-issues")}>Açılış stok kontrol kayıtlarını göster</button>}
          </>}
        </div>}
        {!workflow439.production_requirement.ready && <p className="issue warning">{workflow439.production_requirement.message}</p>}
        {Boolean(workflow439.recipe_learning?.length) && <details>
          <summary>Önceki onaylar · {workflow439.recipe_learning!.filter(row => row.applied).length} eşleşme kullanıldı</summary>
          <div className="smart-answer-table-scroll"><table><thead><tr><th>Ürün / reçete</th><th>Önceki seçim</th><th>Durum</th><th>İşlem</th></tr></thead><tbody>
            {workflow439.recipe_learning!.map(row => <tr key={`${row.bucket}:${row.subject}`}><td>{row.label}</td><td>{row.source} · {row.year}</td>
              <td>{row.applied ? "Önceki onaylı eşleşme kullanıldı" : "Otomatik uygulanmadı; güncel bilgilerle doğrulayın"}</td>
              <td><button type="button" className="text-button" disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy}
                onClick={() => void workflowAction("review_learned_recipe", { bucket: row.bucket, subject: row.subject })}>Eşleşmeyi değiştir</button></td></tr>)}
          </tbody></table></div>
        </details>}
        {!workflow439.material_review?.required && !current439Check && !workflow439.needs_choice && Boolean(guidedQuestions.length) && <p>{guidedQuestions.length} soru grubu. Ortak hammadde sorunları birlikte gösteriliyor. Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>}
        {!workflow439.material_review?.required && !workflow439.needs_choice && Boolean(workflow439.questions.length) && <p>Hammadde gruplarını eşleştirip bildiğiniz dönüşümleri belirtin. HESAPLA bütün seçimleri birlikte kaydeder ve hazır kayıtları FIFO ile işler. Eksik eşleştirme ve dönüşümler kapsam dışı sayılmaz; açık olarak kalır ve hesap tamamlanmış gösterilmez.</p>}

        {!workflow439.material_review?.required && invalidPurchaseAnswers.length > 0 && <p role="alert" className="issue warning">{invalidPurchaseAnswers.length} seçim eksik veya bu aşamada geçersiz. İlgili gruptaki seçimi tamamlayın. Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>}
        {!automaticPreparationBusy && missingRecipeQuestions.length > 0 && <div className="issue warning">
          <strong>{missingRecipeQuestions.length} satış ürününün reçete bağlantısını tamamlayın</strong>
          <p>Kapasite raporu ve reçete Excel’i kontrol edildi. Aşağıdaki ürünlerin geçerli üretim reçetesi henüz belirlenemedi. Mevcut reçeteyi bağlayın veya ürünün yanındaki düğmeyle reçete oluşturun. Aynen alıp satılan ürünlerde al-sat seçin.</p>
          <p>Bu ürünler tamamlanmadan tam kapsamlı iade hesaplanamaz. Reçetelerden sonra alış ve stokların tüketimi karşılayıp karşılamadığı ayrıca kontrol edilir.</p>
        </div>}
        {workflow439.material_review?.required ? null : workflow439.needs_choice ? <div className="smart-guided-actions">
          <p>{workflow439.remaining_count} karar veya bilgi alanı kaldı. Kalan kayıtların tamamını açarak inceleyebilirsiniz; bu işlem hesaplama başlatmaz.</p>
          <button type="button" className="primary-button" disabled={Boolean(busy)} onClick={() => void workflowAction("continue")}>Kalan kayıtların tamamını göster</button>
          <button type="button" className="secondary-button" disabled={Boolean(busy)} onClick={() => void workflowAction("exclude_remaining")}>Kalan belirsizliklerle ilgili kalemleri kapsam dışında bırakıp hesaplanan iadeyi göster</button>
        </div> : <GuidedQuestions caseId={refundCase.id} recipeUpdates={decisionRecipeUpdates} onSaveRecipes={saveDecisionRecipes} renderSourceAction={() => null}
          onPreparedQuestions={questions => setWorkflow439(current => current ? { ...current, questions } : current)}
          onRecipeUpdates={updates => setDecisionRecipeUpdates(current => [...current.filter(item => !updates.some(next => next.recipe_key === item.recipe_key)), ...updates])}
          priorLedgerUpload={workspace.opening_stock_import && <button type="button" className="secondary-button" onClick={onOpenSources}>Önceki dönem şirket e-Defter belgelerini aç</button>}
          key={`${refundCase.id}:${workflow439.round}`} focusQuestion={focusQuestion} onReviewRecipes={() => scrollTo("smart-recipes")} onAddRecipeMaterial={() => { scrollTo("smart-recipes"); setMessage("Malzemeyi ekleyeceğiniz ürünün Reçeteyi düzenle düğmesine basın; ardından Yeni malzeme ekle ile adını, birimini ve ürün başına tüketimini girin."); }} onPurchaseDraftsChange={answers => setPurchaseDrafts(current => ({ ...current, ...answers }))} questions={guidedQuestions} recipes={workspace.purchase_material_recipes || workspace.capacity_recipes} editingLoadingQuestionIds={editingRecipeQuestions} periodStart={refundCase.period_start} disabled={Boolean(busy) || automaticPreparationBusy || anyUploadBusy || Boolean(missingRecipe) || !canPrepare} error={errorContext} onCreateRecipe={setMissingRecipe} renderLoadingAction={renderLoadingReview} renderQuestionFeedback={question => question.id === questionFeedbackId ? <div className="smart-question-feedback">{error && <p className="issue error" role="alert">{error}</p>}{message && <p className="notice" role="status">{message}</p>}</div> : null} onReviewLoading={question => void workflowAction("review_loading", { question_id: question.id, inline: true })} renderRecipeEditor={question => question.kind === "loading" ? renderLoadingRecipeInputs(question) : missingRecipe?.id === question.id ? <MissingRecipeEditor inline question={missingRecipe} refundCase={refundCase} templates={workspace.capacity_recipes}
          onClose={() => setMissingRecipe(null)} onSaved={async () => { setQuestionFeedbackId(question.id); await load(); setMissingRecipe(null); setMessage("Reçete kaydedildi ve onaylandı. Sonraki hesapta alış ve tüketim bağlantıları kontrol edilecek."); }} /> : null} onAnswer={(questionId, value) => void workflowAction("answer", { question_id: questionId, value })} onMemberAnswers={answers => void workflowAction("answer_members", { answers })} />}
        {(missingRecipe || editingRecipeQuestions.length > 0) && <p className="notice">Hesaplamaya geçmeden önce açık reçete düzenlemesini kaydedin veya vazgeçin.</p>}
        {interruptedCalculation && <p className="issue warning" role="alert">Hesaplama kesintiye uğradı. Kaydedilmiş cevaplarınız ve tamamlanan adımlar korundu. Yeniden deneyebilirsiniz.</p>}
        {!automaticPreparationBusy && workspace.latest_result?.status === "blocked" && <div hidden className="issue warning" role="status">
          <strong>Hesaplama durdu; aşağıdaki eksikleri tamamlayın</strong>
          <ul>{actionResultIssues.map(item => <li key={item.code}>{formatMoneyText(item.message)}
            {typeof item.metadata?.invoice_no === "string" && <> · {item.metadata.invoice_no}</>}
          </li>)}</ul>
          {current439Check && <p>Kaynaklar veya seçimler değişmedi. Aynı işlemi tekrar başlatmadan bu eksikleri tamamlayın.</p>}
        </div>}
        {!sectorReady && <p id="smart-439-sector-blocker" className="issue warning" role="status">
          HESAPLA kapalı: GİB sektör seçimi eksik. <button type="button" className="text-button" onClick={() => scrollTo("smart-439-sector")}>İadeye konu faaliyeti seçin</button>
        </p>}
        {showBulkPurchaseExclusion && <div>
          <button className="secondary-button danger" type="button"
            disabled={!canPrepare || Boolean(busy) || automaticPreparationBusy || anyUploadBusy || purchaseExclusionsSelected}
            onClick={() => void workflowAction("answer_members", { answers: Object.fromEntries(purchaseExclusionQuestions.map(question => [question.id, "exclude"])) })}>
            {purchaseExclusionsSelected ? `${purchaseExclusionCount} alış satırı için toplu kapsam dışı seçildi` : `${purchaseExclusionCount} alış satırını toplu kapsam dışı bırak`}
          </button>
          <p>Seçim yalnız bu iade dosyasına uygulanır; faturalar silinmez. HESAPLA ile kaydedilen seçim uygulanır.</p>
        </div>}
        {actionRequiredPanel}
        {(!current439Check || Boolean(workspace.latest_result)) && !workflow439.needs_choice && <button className="primary-button" type="button" aria-describedby={!sectorReady ? "smart-439-sector-blocker" : !required439SourcesReady ? "smart-439-source-blocker" : undefined} disabled={!calculationReady || Boolean(busy) || anyUploadBusy} onClick={() => void calculate("final")}>{automaticPreparationBusy ? `${String(automaticJob.result?.stage || "Hesaplanıyor")} · %${automaticJob.progress}` : interruptedCalculation ? "Hesaplamayı yeniden dene" : "HESAPLA"}</button>}
        {workflow439.limited_scope && <button className="secondary-button" type="button" disabled={Boolean(busy) || automaticPreparationBusy || anyUploadBusy} onClick={() => void workflowAction("resume")}>Ek soruları cevaplayarak kapsamı tamamla</button>}
        </div>
        {automaticPreparationBusy && <div className="smart-job-progress" role="status">
          <strong>{automaticJob.status === "queued" ? "Hesaplama sırası bekleniyor" : String(automaticJob.result?.stage || "Kaynaklar hesaplanıyor")}</strong>
          <progress max={100} value={automaticJob.progress} aria-label="Hesaplama ilerlemesi" />
          <span>%{automaticJob.progress} · İşlem sürüyor; sayfa tamamlandığında kendiliğinden güncellenecek.</span>
        </div>}
      </section>}

      {!is439 && <>
      {requiresExportChain && workspace.product_identity_mapping.unresolved_count > 0 && <section className="panel smart-product-identity-panel" id="smart-product-identities">
        <div className="panel-head">
          <div><p className="eyebrow">ÜRÜN KODU SÖZLÜĞÜ</p><h2>ERP ürününü fatura ürünüyle eşleştirin</h2></div>
          <em className="pill partial">{workspace.product_identity_mapping.unresolved_count} hareket bekliyor</em>
        </div>
        <p className="smart-optional-note">İsim, GTİP, birim ve geçmiş eşleşmeler yalnız öneri sırasını belirler. Benzer isim nedeniyle kullanıcı onayı olmadan eşleşme yapılmaz.</p>
        <div className="smart-product-identity-list">
          {workspace.product_identity_mapping.groups.map((group) => <article key={group.key}>
            <span><strong>{group.inventory_code} · {group.name}</strong><small>{group.direction === "out" ? "Satış" : "Alış"} · {group.unit || "birim yok"}{group.gtip ? ` · GTİP ${group.gtip}` : ""} · {group.document_nos.join(", ") || "belge no yok"}</small></span>
            {group.candidates.length ? <label>Fatura ürünü
              <select value={identitySelections[group.key] || ""} onChange={(event) => setIdentitySelections((current) => ({ ...current, [group.key]: event.target.value }))}>
                <option value="">Seçin</option>
                {group.candidates.map((candidate) => <option key={candidate.invoice_line_id} value={candidate.invoice_line_id}>{candidate.invoice_code} · {candidate.name} · %{candidate.score} — {candidate.reasons.join(", ")}</option>)}
              </select>
            </label> : <div className="notice warning">Uygun fatura satırı bulunamadı. Belge numarasını ve fatura ürün kodunu kontrol edin.</div>}
            <button className="primary-button compact" disabled={!identitySelections[group.key] || Boolean(busy)} onClick={() => confirmProductIdentity(group.key)} type="button">Onayla ve kalıcı kaydet</button>
          </article>)}
        </div>
      </section>}

      {is338 && <section className="panel smart-loading-engine" id="smart-loading-engine">
        <div className="panel-head">
          <div><p className="eyebrow">338 · ÖZEL SINIR</p><h2>KDV'siz temin edilen girdiler</h2></div>
          <em className={`pill ${vatFreeConfirmed ? "active" : "partial"}`}>{vatFreeConfirmed ? "Doğrulandı" : "Teyit gerekli"}</em>
        </div>
        <div className="smart-loading-actions">
          <article>
            <strong>KDV ödenmeden temin edilen girdi bedeli</strong>
            <small>Yurt içinden veya ithalat yoluyla KDV ödenmeden alınan girdilerin bedelini yazın; yoksa 0 olarak doğrulayın. Bu bedel %10 hesabından düşülür.</small>
            <label>Doğrulanmış tutar<MoneyInput value={vatFreeInputBase} onChange={(value) => { setVatFreeInputBase(value); setVatFreeConfirmed(false); }} /></label>
            <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy) || vatFreeInputBase === ""} type="button" onClick={() => loadingAction("confirm_vat_free_input", { amount: vatFreeInputBase })}>{vatFreeConfirmed ? "Doğrulandı" : "Durumu doğrula"}</button>
          </article>
        </div>
      </section>}

      {requiresExportChain && <section className="smart-result-grid">
        <article className="smart-result-card primary">
          <span>{refundCase.transaction_code} ihracat kaynak zinciri</span>
          <strong>{money(summary?.export_delivery_amount_try || "0")}</strong>
          <small>{summary?.export_invoice_count || 0} fatura · {summary?.export_sales_line_count || 0} GTİP satırı · {summary?.export_customs_count || 0} GÇB/ETGB</small>
        </article>
        <article className={`smart-result-card ${summary?.kdv1_ready ? "primary" : ""}`}>
          <span>KDV1 mutabakatı</span>
          <strong>{summary?.kdv1_ready ? "Mutabık" : "Kontrol gerekli"}</strong>
          <small>{workspace.kdv1_reconciliation?.period || refundCase.period_start.slice(0, 7)}</small>
        </article>
      </section>}

      {requiresExportChain && kdv1Issues.length > 0 && <section className="panel smart-warning-list">
        <div className="panel-head">
          <div><p className="eyebrow">KDV1 MUTABAKATI</p><h2>Beyanname ile hesap arasında fark var</h2></div>
          <small>Nihai hesap, bütün farklar giderilene kadar kapalıdır.</small>
        </div>
        {kdv1Issues.map((item) => <div className="issue error" key={item.code}>
          <strong>{formatMoneyText(item.message)}</strong>
        </div>)}
      </section>}

      {is301 && <section className="panel smart-loading-engine" id="smart-loading-engine">
        <div className="panel-head">
          <div><p className="eyebrow">301 · YÜKLENİM MOTORU</p><h2>Gerçek yüklenim ve izlenebilir dağıtımlar</h2></div>
          <em className={`pill ${workspace.loading_engine?.ready ? "active" : "partial"}`}>{workspace.loading_engine?.ready ? "Doğrudan iz hazır" : "Stok izi gerekli"}</em>
        </div>
        <div className="smart-calculation-result">
          <span><small>1 · Doğrudan</small><strong>{money(workspace.loading_engine?.totals.direct || "0")}</strong></span>
          <span><small>2 · Genel gider</small><strong>{money(workspace.loading_engine?.totals.general || "0")}</strong></span>
          <span><small>3 · ATİK</small><strong>{money(workspace.loading_engine?.totals.atik || "0")}</strong></span>
        </div>
        {workspace.loading_engine?.blockers
          .filter((item) => item.code !== "vat-free-input-unconfirmed" || Boolean(summary?.inventory_movement_count))
          .map((item) => <div className="issue error" key={item.code}>{formatMoneyText(item.message)}</div>)}
        <div className="smart-loading-actions">
          {!workspace.loading_engine?.ready && <article>
            <strong>1 No.lu · Gerçek stok izi</strong>
            <small>Yalnız GÇB/ETGB ile doğrulanmış ihracat faturalarına ulaşan alış → stok/sarf → üretim → satış miktarı dikkate alınır.</small>
            <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy) || anyUploadBusy} type="button" onClick={() => loadingAction("prepare_direct", {})}>{busy === "loading-prepare_direct" ? "Hazırlanıyor…" : "Stok izinden hazırla"}</button>
          </article>}
          {!vatFreeConfirmed && Boolean(summary?.inventory_movement_count) && <article>
            <strong>KDV’siz alınmış girdi bulundu</strong>
            <small>Yüklenen alış kayıtlarında KDV’siz satır bulunduğu için yalnız bu istisnanın ihracata ait tutarını doğrulayın.</small>
            <label>İhracata ait KDV’siz girdi/hizmet bedeli<MoneyInput value={vatFreeInputBase} onChange={(value) => setVatFreeInputBase(value)} /></label>
            <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy) || vatFreeInputBase === ""} type="button" onClick={() => loadingAction("confirm_vat_free_input", { amount: vatFreeInputBase })}>Tutarı doğrula</button>
          </article>}
          {generalCandidates.length > 0 && !noGeneralConfirmed && <article>
            <strong>2 No.lu · Genel gider payı</strong>
            <small>Yalnız sistemin genel gider adayı bulduğu için gösteriliyor.</small>
            <label>Belge satırı<select value={generalAllocation.invoice_line_id} onChange={(event) => setGeneralAllocation({ ...generalAllocation, invoice_line_id: event.target.value })}><option value="">Seçin</option>{generalCandidates.map((item) => <option key={item.invoice_line_id} value={item.invoice_line_id}>{item.invoice_no} · {item.item_name} · {money(item.vat_amount)}</option>)}</select></label>
            <label>Dağıtım anahtarı<select value={generalAllocation.basis_kind} onChange={(event) => setGeneralAllocation({ ...generalAllocation, basis_kind: event.target.value })}><option value="turnover">Ciro</option><option value="direct_cost">Doğrudan maliyet</option><option value="quantity">Miktar</option><option value="manual">Diğer doğrulanmış anahtar</option></select></label>
            <label>İhracata düşen pay{["turnover", "direct_cost"].includes(generalAllocation.basis_kind)
              ? <MoneyInput value={generalAllocation.numerator} onChange={value => setGeneralAllocation({ ...generalAllocation, numerator: value })} />
              : <input inputMode="decimal" value={generalAllocation.numerator} onChange={(event) => setGeneralAllocation({ ...generalAllocation, numerator: event.target.value })} />}</label>
            <label>Toplam dağıtım tutarı{["turnover", "direct_cost"].includes(generalAllocation.basis_kind)
              ? <MoneyInput value={generalAllocation.denominator} onChange={value => setGeneralAllocation({ ...generalAllocation, denominator: value })} />
              : <input inputMode="decimal" value={generalAllocation.denominator} onChange={(event) => setGeneralAllocation({ ...generalAllocation, denominator: event.target.value })} />}</label>
            <label>Gerekçe<textarea value={generalAllocation.explanation} onChange={(event) => setGeneralAllocation({ ...generalAllocation, explanation: event.target.value })} /></label>
            <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy) || !generalAllocation.invoice_line_id || !generalAllocation.numerator || !generalAllocation.denominator || generalAllocation.explanation.trim().length < 3} type="button" onClick={() => loadingAction("allocate_general", generalAllocation)}>Payı hesapla ve kaydet</button>
          </article>}
          {atikCandidates.length > 0 && !noAtikConfirmed && <article>
            <strong>3 No.lu · Kümülatif ATİK</strong>
            <small>Yalnız sistemin sabit kıymet adayı bulduğu için gösteriliyor.</small>
            <label>ATİK belge satırı<select value={atikAllocation.invoice_line_id} onChange={(event) => setAtikAllocation({ ...atikAllocation, invoice_line_id: event.target.value })}><option value="">Seçin</option>{atikCandidates.map((item) => <option key={item.invoice_line_id} value={item.invoice_line_id}>{item.invoice_no} · {item.item_name} · {money(item.vat_amount)}</option>)}</select></label>
            <label>Varlık / sabit kıymet referansı<input value={atikAllocation.asset_reference} onChange={(event) => setAtikAllocation({ ...atikAllocation, asset_reference: event.target.value })} /></label>
            <label>Fiilen kullanıma başlama ayı<input type="month" value={atikAllocation.use_start_period} onChange={(event) => setAtikAllocation({ ...atikAllocation, use_start_period: event.target.value })} /></label>
            <label>Kümülatif iade hakkı doğuran işlem<MoneyInput value={atikAllocation.cumulative_refund_turnover} onChange={(value) => setAtikAllocation({ ...atikAllocation, cumulative_refund_turnover: value })} /></label>
            <label>Kümülatif toplam işlem<MoneyInput value={atikAllocation.cumulative_total_turnover} onChange={(value) => setAtikAllocation({ ...atikAllocation, cumulative_total_turnover: value })} /></label>
            <label>VRP öncesi dönemlerde kullanılan pay (yoksa 0)<MoneyInput value={atikAllocation.opening_prior_allocated_vat} onChange={(value) => setAtikAllocation({ ...atikAllocation, opening_prior_allocated_vat: value })} /></label>
            <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy) || !atikAllocation.invoice_line_id || !atikAllocation.asset_reference.trim() || !atikAllocation.cumulative_refund_turnover || !atikAllocation.cumulative_total_turnover || atikAllocation.opening_prior_allocated_vat === ""} type="button" onClick={() => loadingAction("allocate_atik", atikAllocation)}>Kümülatif payı hesapla</button>
          </article>}
        </div>
        {(workspace.loading_engine?.allocations || []).length > 0 && <div className="smart-candidate-list">{workspace.loading_engine?.allocations.map((item) => <article key={item.id}><span><strong>{item.description}</strong><small>{item.invoice_no} · yöntem {item.method}</small></span><span><strong>{money(item.eligible_vat)}</strong><small>oran %{(Number(item.allocation_ratio) * 100).toLocaleString("tr-TR")}</small></span><em className="pill active">{item.load_type === "direct" ? "1 No.lu" : item.load_type === "general" ? "2 No.lu" : "3 No.lu"}</em></article>)}</div>}
      </section>}

      <section className="smart-result-grid">
        <article className="smart-result-card primary">
          <span>1 No.lu doğrudan yüklenim · hesaba alınan</span>
          <strong>{money(summary?.direct_ready_vat || "0")}</strong>
          <small>{requiresExportChain ? `${summary?.direct_ready_count || 0} gerçek stok/sarf/ürün izi; kapasite tek başına dahil edilmez` : `${summary?.direct_auto_ready_count || 0} otomatik · ${summary?.direct_accepted_count || 0} açıklamayla kabul · ${money(summary?.direct_review_vat || "0")} henüz iadeye dahil değil`}</small>
        </article>
        <article className="smart-result-card">
          <span>Fatura analizi</span>
          <strong>{summary?.classified_line_count || 0} / {summary?.invoice_line_count || 0}</strong>
          <small>{summary?.unresolved_line_count || 0} satır belirsiz · kalanlar otomatik ayrıldı</small>
        </article>
        <article className="smart-result-card">
          <span>Kullanıcı kontrolü</span>
          <strong>{reviewCount}</strong>
          <small>{hardCapacityIssues.length || recipeCapacityIssues.length ? `${hardCapacityIssues.length + recipeCapacityIssues.length} üretim/reçete kontrolü var` : "Yalnız gerekli istisnalar"}</small>
        </article>
      </section>

      {(workspace.categories || []).length > 0 && <details className="panel smart-analysis-summary smart-collapsible" id="smart-invoice-intelligence">
        <summary className="panel-head">
          <div><p className="eyebrow">FATURA ZEKÂSI</p><h2>Sistem ne anladı?</h2></div>
          <span className="smart-collapse-meta"><small>Kullanıcı seçimi gerekmez</small><i aria-hidden="true">⌄</i></span>
        </summary>
        <div className="smart-collapsible-content"><div className="smart-category-grid">
          {workspace.categories.map((item) => <article key={item.category}>
            <strong>{item.label}</strong>
            <span>{item.line_count} satır</span>
            <small>{money(item.vat_amount)} KDV</small>
          </article>)}
        </div></div>
      </details>}

      {visibleExceptions.length > 0 && <section className="panel smart-exception-panel" id="smart-required-reviews">
        <div className="panel-head">
          <div><p className="eyebrow">KULLANICIDAN BEKLENEN</p><h2>Yalnızca istisnalar</h2></div>
          <div className="smart-required-review-head">
            <small>Muavin yanlışsa düzeltmesini yükleyin; hesap doğruysa kısa açıklamayla kabul edin.</small>
            {backgroundReviewCount > 0 && <em className="pill active">{backgroundReviewCount} arka planda kaydediliyor</em>}
            <em className="pill partial">{reviewCount} satır</em>
          </div>
        </div>
        <div className="smart-candidate-list">
          {visibleExceptions.map((item) => <article id={`capacity-exception-${item.invoice_line_id}`} key={`exception-${item.invoice_line_id}`}>
            <span><strong>{item.item_name}</strong><small>{item.invoice_no} · {item.supplier}</small></span>
            <span><strong>{item.category_label}</strong><small>{item.classification_reason}</small></span>
            <span><strong>{money(item.vat_amount)}</strong><small>{item.account_codes.length ? `Hesap ${item.account_codes.map(formatAccountCode).join(", ")}` : "Muhasebe hesabı bulunamadı"}</small></span>
            <em className="pill partial">Kontrol</em>
            <small className="smart-candidate-issue">{item.issues.join(" · ")}</small>
            <div className="smart-exception-actions">
              <button className="secondary-button compact" onClick={onOpenSources}>Şirketin muavin belgelerini aç</button>
              {item.reviewable && <button
                className="secondary-button compact"
                disabled={!canPrepare || Boolean(busy) || anyUploadBusy || savingReviewLineIds.includes(item.invoice_line_id)}
                type="button"
                onClick={() => {
                  setReviewingLineId(item.invoice_line_id);
                  setReviewNote("");
                }}
              >
                {item.account_codes.length ? `${item.account_codes.map(formatAccountCode).join(", ")} doğru, açıklamayla devam et` : "Açıklamayla devam et"}
              </button>}
            </div>
            {reviewingLineId === item.invoice_line_id && <div className="smart-review-form">
              {<label>Açıklama<textarea autoFocus value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} placeholder="Bu hesabın neden doğru olduğunu kısaca yazın." /></label>}
              <div>
                <button aria-live="polite" className="primary-button compact" disabled={Boolean(busy) || anyUploadBusy || savingReviewLineIds.includes(item.invoice_line_id)} type="button" onClick={() => submitExceptionReview(item)}>{savingReviewLineIds.includes(item.invoice_line_id) ? "Kaydediliyor…" : "Kaydet ve devam et"}</button>
                <button className="secondary-button compact" disabled={Boolean(busy) || anyUploadBusy || savingReviewLineIds.includes(item.invoice_line_id)} type="button" onClick={() => { setReviewingLineId(null); setReviewNote(""); }}>Vazgeç</button>
              </div>
            </div>}
          </article>)}
        </div>
      </section>}

      {analysis && <details className="panel smart-capacity-summary smart-collapsible" id="smart-capacity-report">
        <summary className="panel-head">
          <div><p className="eyebrow">KAPASİTE RAPORU</p><h2>{analysis.company_title || workspace.capacity_report?.filename}</h2></div>
          <span className="smart-collapse-meta"><i aria-hidden="true">⌄</i></span>
        </summary>
        <div className="smart-collapsible-content">

        <small>VKN {analysis.vkn || "okunamadı"} · geçerlilik {analysis.valid_until || "okunamadı"} · {analysis.page_count} sayfa · güven {analysis.confidence}</small>
        {reportCapacityIssues.map((item) => <div className={`issue ${item.severity}`} key={item.code}>{formatMoneyText(item.message)}</div>)}
        <div className="smart-capacity-columns">
          <div><strong>Mamuller</strong>{analysis.outputs.map((item) => <span key={`${item.code}-${item.name}`}><b>{item.name}</b><small>{quantity(item)}</small></span>)}</div>
          <div><strong>Girdiler</strong>{analysis.inputs.map((item) => <span key={`${item.code}-${item.name}`}><b>{item.name}</b><small>{quantity(item)} · {capacityRole(item)}</small></span>)}</div>
        </div>
        </div>
      </details>}

      </>}
      {!tradeOnly && (analysis || workspace.capacity_recipe_import) && <details className="panel smart-recipe-panel smart-collapsible" id="smart-recipes">
        <summary className="panel-head">
          <div><p className="eyebrow">REÇETELER</p><h2>{companyRecipeEditing ? "Şirket Excel’indeki ürün reçeteleri" : "Kapasite raporundan oluşturulan ürün reçeteleri"}</h2></div>
          <span className="smart-collapse-meta"><em className={`pill ${recipeCapacityIssues.length ? "partial" : "active"}`}>{capacityRecipes.length} reçete</em><i aria-hidden="true">⌄</i></span>
        </summary>
        <div className="smart-collapsible-content">
          <div className="smart-recipe-actions">
            {!companyRecipeEditing && <button
              className="secondary-button compact"
              disabled={!canPrepare || Boolean(busy) || editingRecipeKey !== null || !analysis?.outputs.length}
              type="button"
              onClick={() => {
                const product = analysis?.outputs[0];
                if (!product) return;
                setNewRecipeProduct(`${product.code}|${product.name}|${product.unit}`);
                setRecipeDraft([]);
                setEditingRecipeKey("__new__");
              }}
            >Yeni reçete ekle</button>}
            {workspace.capacity_recipes.length > 0 && <button type="button" className="secondary-button compact" disabled={Boolean(busy) || automaticPreparationBusy || refreshingResult} onClick={() => window.location.assign(`/api/refund-cases/${refundCase.id}/capacity-recipes/export`)}>Son halini indir</button>}
          </div>
        <p className="smart-optional-note">{companyRecipeEditing ? "Buradaki miktarlar HESAPLA’nın kullandığı şirket reçetesidir. Değişiklik, bu reçeteye bağlı ürünlere uygulanır. Yeni ürün veya hammadde eklemek, silmek ya da birimlerini değiştirmek için reçete Excel’ini güncelleyip yükleyin." : "Birim üretim tüketimlerini düzenleyebilirsiniz. Son reçeteler kapasite sınırı kontrollerinde kullanılır."}</p>
        {!is439 && <section className="smart-recipe-create" id="smart-material-mappings">
          <h3>Hammaddeyi alış ürünüyle eşleştir</h3>
          <p>Sistem eşleştirmeleri otomatik değerlendirir. Buradan sonuçları değiştirebilir veya tek hammadde için araştırmayı yeniden çalıştırabilirsiniz.</p>
          <button className="secondary-button compact" type="button" disabled={Boolean(busy)} onClick={openMaterialMappings}>{busy === "load-material-mappings" ? "Yükleniyor…" : "Manuel eşleştirmeyi aç / yenile"}</button>
          {materialMappings && <>
            {!materialMappings.materials.length ? <p>Eşleştirme bekleyen hammadde yok.</p> : <>
              <label>Reçetedeki hammadde<select value={selectedMaterial} onChange={(event) => { setSelectedMaterial(event.target.value); setSelectedPurchase(proposedPurchase(materialMappings, event.target.value)); setSmartMaterialResult(null); }} disabled={Boolean(busy)}>
                <option value="">Hammadde seçin</option>
                {materialMappings.materials.map((item) => <option key={item.id} value={item.id}>{item.code} · {item.name} · {item.base_unit}</option>)}
              </select></label>
              <label>Alış ürün kodu veya adıyla ara<input value={purchaseSearch} onChange={(event) => { setPurchaseSearch(event.target.value); setSelectedPurchase(""); }} disabled={Boolean(busy)} /></label>
              <label>Alış ürünü<select value={selectedPurchase} onChange={(event) => setSelectedPurchase(event.target.value)} disabled={Boolean(busy)}>
                <option value="">Alış ürünü seçin (ilk 100 sonuç; arayarak daraltın)</option>
                {materialMappings.candidates.filter((item) => `${item.code} ${item.name}`.toLocaleLowerCase("tr-TR").includes(purchaseSearch.toLocaleLowerCase("tr-TR"))).slice(0, 100).map((item) => <option key={item.id} value={item.id}>{item.code} · {item.name} · {(item.source_units || [item.base_unit]).join(", ")}</option>)}
              </select></label>
              <div className="smart-recipe-actions">
                <button className="primary-button compact" type="button" disabled={!canPrepare || Boolean(busy) || !selectedMaterial || !selectedPurchase} onClick={() => matchMaterial(false)}>Manuel eşleştir</button>
                <button className="secondary-button compact" type="button" disabled={!canPrepare || Boolean(busy) || !selectedMaterial} onClick={() => matchMaterial(true)}>{busy === "smart-material" ? "OpenAI web kaynaklarını araştırıyor…" : "Akıllı eşleştir"}</button>
              </div>
              <small>Bu buton şirket adını, üretilen ürünleri ve ilgili alış ürünlerinin kod/açıklamalarını OpenAI’ye gönderir; API ücreti oluşabilir. Faturaların toplam ve KDV tutar alanları gönderilmez; API anahtarı tarayıcıya aktarılmaz. Web araştırmasıyla desteklenen, birimi uyumlu ve güven tahmini %60’ın üzerinde olan eşleşme kaydedilir. Diğerleri manuel kalır. Bu yüzde kesinlik veya iade onayı değildir.</small>
            </>}
          </>}
          {smartMaterialResult && <div className={`issue ${smartMaterialResult.applied ? "warning" : "error"}`} role="status">
            <strong>{smartMaterialResult.applied ? "Eşleşme kaydedildi" : "Eşleşme uygulanmadı; kullanıcı teyidi gerekli"}{smartMaterialResult.confidence != null ? ` · AI güven tahmini %${smartMaterialResult.confidence}` : ""}</strong>
            {smartMaterialResult.candidate && <p>{smartMaterialResult.candidate.code} · {smartMaterialResult.candidate.name}</p>}
            <p>{smartMaterialResult.reason}</p>
            {smartMaterialResult.ambiguous && <p>Birden fazla olası karşılık var; güven tahmini yüksek olsa bile otomatik eşleştirilmedi.</p>}
            <small>{smartMaterialResult.candidate_count} alış adayı değerlendirildi. Belirsiz, %60 ve altı veya doğrulanabilir web kaynağı olmayan sonuçlar uygulanmaz.</small>
            {smartMaterialResult.sources.map((url) => <p key={url}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></p>)}
            {smartMaterialResult.applied && <button className="secondary-button compact danger" type="button" disabled={!canPrepare || Boolean(busy)} onClick={undoSmartMaterial}>Bu akıllı eşleşmeyi geri al</button>}
          </div>}
        </section>}
        {editingRecipeKey === "__new__" && <article className="smart-recipe-create">
          <label>Mamul
            <select value={newRecipeProduct} onChange={(event) => setNewRecipeProduct(event.target.value)}>
              {(analysis?.outputs || []).map((item) => <option key={`${item.code}-${item.name}-${item.unit}`} value={`${item.code}|${item.name}|${item.unit}`}>{item.name} · yıllık en fazla {Number(item.annual_quantity).toLocaleString("tr-TR")} {item.unit}</option>)}
            </select>
          </label>
          <div className="smart-recipe-components">
            {recipeDraft.map((item) => <label className="editable" key={item.draft_key}>
              <select value={`${item.material_code}|${item.material_name}|${item.material_unit}`} onChange={(event) => changeDraftMaterial(item.draft_key, event.target.value)}>
                {(analysis?.inputs || []).filter((row) => row.economic_role === "direct_material").map((row) => <option key={`${row.code}-${row.name}-${row.unit}`} value={`${row.code}|${row.name}|${row.unit}`}>{row.name} · {row.unit}</option>)}
              </select>
              <input aria-label={`${item.material_name} birim tüketimi`} min="0.000001" step="any" type="number" value={item.material_quantity} onChange={(event) => setRecipeDraft((current) => current.map((row) => row.draft_key === item.draft_key ? { ...row, material_quantity: event.target.value } : row))} />
              <button className="secondary-button compact" type="button" onClick={() => setRecipeDraft((current) => current.filter((row) => row.draft_key !== item.draft_key))}>Hammaddeyi sil</button>
            </label>)}
          </div>
          <div className="smart-recipe-actions">
            <button className="secondary-button compact" type="button" onClick={addRecipeComponent}>Hammadde ekle</button>
            <button className="primary-button compact" disabled={Boolean(busy) || !newRecipeProduct || recipeDraft.length === 0 || recipeDraft.some((item) => !item.material_quantity || Number(item.material_quantity) <= 0)} type="button" onClick={createRecipe}>{busy === "create-capacity-recipe" ? "Kaydediliyor…" : "Yeni reçeteyi kaydet"}</button>
            <button className="secondary-button compact" disabled={Boolean(busy)} type="button" onClick={() => { setEditingRecipeKey(null); setNewRecipeProduct(""); setRecipeDraft([]); }}>Vazgeç</button>
          </div>
        </article>}
        {workspace.capacity_recipes.length > 0 ? <div className="smart-recipe-list">
          {capacityRecipes.map(({ recipeKey, product, components }) => {
            const editing = editingRecipeKey === recipeKey;
            const expanded = editing || expandedRecipeKeys.includes(recipeKey);
            const edited = components.some((item) => item.edited);
            const quantityPending = components.some(item => item.quantity_pending);
            const displayedComponents: RecipeComponentDraft[] = editing ? recipeDraft : components.map((item) => ({
              draft_key: item.component_key,
              component_key: item.component_key,
              material_code: item.material_code,
              material_name: item.material_name,
              material_unit: item.material_unit,
              material_quantity: item.material_quantity
            }));
            return <article key={recipeKey}>
              <div className="smart-recipe-title">
                <span><strong>{product.product_name}</strong><small>{product.product_code || "Ürün kodu yok"}{product.annual_output_quantity ? ` · yıllık en fazla ${Number(product.annual_output_quantity).toLocaleString("tr-TR")} ${product.output_unit}` : " · yıllık kapasite sınırı belirtilmemiş"}</small></span>
                {quantityPending ? <em className="pill">Miktar ve birim bekliyor</em> : edited && <em className="pill active">Düzenlendi</em>}
              </div>
              {expanded && <div className="smart-recipe-components">
                {displayedComponents.map((item) => {
                  const sourceLabel = components.find(component => component.component_key === item.component_key)?.source_label;
                  return <label className={editing ? "editable" : ""} key={item.draft_key}>
                  {editing && item.custom_material ? <span>
                    <input aria-label="Yeni malzeme adı" placeholder="Malzeme adı" value={item.material_name} onChange={event => setRecipeDraft(current => current.map(row => row.draft_key === item.draft_key ? { ...row, material_name: event.target.value } : row))} />
                    <select aria-label="Yeni malzeme birimi" value={item.material_unit} onChange={event => setRecipeDraft(current => current.map(row => row.draft_key === item.draft_key ? { ...row, material_unit: event.target.value } : row))}>
                      {[["C62", "Adet"], ["KGM", "Kilogram"], ["GRM", "Gram"], ["TNE", "Ton"], ["LTR", "Litre"], ["MTR", "Metre"], ["MTK", "Metrekare"], ["MTQ", "Metreküp"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </span> : editing && !item.component_key ? <select value={`${item.material_code}|${item.material_name}|${item.material_unit}`} onChange={(event) => changeDraftMaterial(item.draft_key, event.target.value)}>
                    {(analysis?.inputs || []).filter((row) => row.economic_role === "direct_material").map((row) => <option key={`${row.code}-${row.name}-${row.unit}`} value={`${row.code}|${row.name}|${row.unit}`}>{row.name} · {row.unit}</option>)}
                  </select> : <span><strong>{item.material_name}</strong><small>{item.material_code || "Hammadde kodu yok"}</small></span>}
                  {editing ? <>
                    <input aria-label={`${item.material_name} birim tüketimi`} min="0.000001" step="any" type="number" value={item.material_quantity} onChange={(event) => setRecipeDraft((current) => current.map((row) => row.draft_key === item.draft_key ? { ...row, material_quantity: event.target.value } : row))} />
                    {!companyRecipeEditing && <button className="secondary-button compact" type="button" onClick={() => setRecipeDraft((current) => current.filter((row) => row.draft_key !== item.draft_key))}>Hammaddeyi sil</button>}
                  </> : <b>{item.material_quantity ? Number(item.material_quantity).toLocaleString("tr-TR", { maximumFractionDigits: 8 }) : "Miktar belirtilmemiş"}</b>}
                  <small>{item.material_unit || "Tüketim birimi doğrulanmadı"} / {product.output_quantity} {product.output_unit || "ürün"}</small>
                  {sourceLabel && <small>{sourceLabel}</small>}
                </label>;
                })}
              </div>}
              {recipeCapacityIssues.filter((issue) => components.some((item) => item.material_name === issue.details?.capacity_name)).map((issue) => <div className="issue warning" key={`${recipeKey}-${issue.code}`}><strong>{formatMoneyText(issue.message)}</strong><small>{issue.code === "capacity-sale-link-missing" ? "Satış ürününün stok kodunu doğru kapasite grubuna bağlayın ve satış miktarının ürün birimine çevrilebildiğini kontrol edin." : "Rapordan aktarılan birim tüketim miktarını kontrol edip reçeteyi düzenleyin."}</small></div>)}
              <div className="smart-recipe-actions">
                {editing ? <>
                  {!companyRecipeEditing && <button className="secondary-button compact" type="button" onClick={addRecipeComponent}>Hammadde ekle</button>}
                  {!companyRecipeEditing && <button className="secondary-button compact" type="button" onClick={() => setRecipeDraft(current => [...current, { draft_key: `new-${Date.now()}-${current.length}`, component_key: "", custom_material: true, material_code: "", material_name: "", material_unit: "C62", material_quantity: "" }])}>Yeni malzeme ekle</button>}
                  <button className="primary-button compact" disabled={Boolean(busy) || recipeDraft.length === 0 || recipeDraft.some((item) => !item.material_name.trim() || !item.material_quantity || Number(item.material_quantity) <= 0)} type="button" onClick={() => saveRecipe(recipeKey)}>{busy === "save-capacity-recipe" ? "Kaydediliyor…" : "Kaydet ve hesaplamada kullan"}</button>
                  <button className="secondary-button compact" disabled={Boolean(busy)} type="button" onClick={() => { setEditingRecipeKey(null); setRecipeDraft([]); }}>Vazgeç</button>
                </> : <>
                  <button className="secondary-button compact" disabled={Boolean(busy)} type="button" onClick={() => setExpandedRecipeKeys((current) => current.includes(recipeKey) ? current.filter((key) => key !== recipeKey) : [...current, recipeKey])}>{expanded ? "Hammaddeleri gizle" : `Hammaddeleri göster (${components.length})`}</button>
                  <button className="secondary-button compact" disabled={(!analysis && !companyRecipeEditing) || !canPrepare || Boolean(busy) || editingRecipeKey !== null} type="button" onClick={() => editRecipe(recipeKey)}>Reçeteyi düzenle</button>
                  {!companyRecipeEditing && <button className="secondary-button compact danger" disabled={!analysis || !canPrepare || Boolean(busy) || editingRecipeKey !== null} type="button" onClick={() => deleteRecipe(recipeKey, product.product_name)}>Reçeteyi sil</button>}
                </>}
              </div>
            </article>;
          })}
        </div> : <div className="empty-state"><span>—</span><p>Kapasite raporundan reçete ilişkisi çıkarılamadı.</p></div>}
        </div>
      </details>}

      {!is439 && <>
      {!requiresExportChain && <details className="panel smart-candidate-panel smart-collapsible" id="smart-direct-candidates">
        <summary className="panel-head"><div><p className="eyebrow">YÜKLENİLEN KDV · TÜR 1</p><h2>Kapasiteyle eşleşen doğrudan girdiler</h2></div><span className="smart-collapse-meta"><em className={`pill ${candidateIssueGroups.length ? "partial" : "active"}`}>{summary?.direct_candidate_count || 0} satır</em><i aria-hidden="true">⌄</i></span></summary>
        <div className="smart-collapsible-content">
        {candidateIssueGroups.length > 0 && <div className="smart-candidate-issue-groups">
          {candidateIssueGroups.map(({ issue, items }) => <details key={issue}>
            <summary><strong>{issue}</strong><em className="pill partial">{items.length} satır</em></summary>
            <small>{Array.from(new Set(items.map((item) => item.invoice_no))).join(", ")}</small>
          </details>)}
        </div>}
        {!workspace.capacity_report ? <div className="empty-state"><span>—</span><p>Kapasite raporu yüklendiğinde fatura eşleştirmeleri burada oluşur.</p></div> : visibleCandidates.length ? <div className="smart-candidate-list">
          {visibleCandidates.map((item) => <article key={item.invoice_line_id}>
            <span><strong>{item.item_name}</strong><small>{item.invoice_no} · {item.supplier}</small></span>
            <span><strong>{item.matched_capacity_input}</strong><small>{Number(item.quantity).toLocaleString("tr-TR")} {item.unit} · eşleşme %{Math.round(item.match_score * 100)}</small></span>
            <span><strong>{money(item.vat_amount)}</strong><small>{item.account_codes.length ? `Hesap ${item.account_codes.map(formatAccountCode).join(", ")}` : "Muhasebe kaydı bekleniyor"}</small></span>
            <em className={`pill ${item.status === "ready" || item.status === "accepted" ? "active" : "partial"}`}>{item.status === "ready" ? "Otomatik hazır" : item.status === "accepted" ? "Açıklamayla kabul" : "Doğrulama gerekli"}</em>
          </article>)}
        </div> : <div className="empty-state"><span>—</span><p>Kapasite girdileriyle eşleşen alış faturası bulunamadı.</p></div>}
        </div>
      </details>}

      {is301 && productionIssues.length > 0 && <section className="panel smart-warning-list" id="smart-production-readiness">
        <div className="panel-head">
          <div><p className="eyebrow">301 · NİHAİ HESAP KONTROLÜ</p><h2>{workspace.production_readiness?.ready ? "Nihai hesaba hazır" : "Tamamlanması gerekenler"}</h2></div>
          <small>Taslak hesap bu kontroller tamamlanmadan da görülebilir; nihai hesap oluşturulamaz.</small>
        </div>
        {productionIssues.map((item) => <div className={`issue ${item.severity}`} key={item.code}>
          <strong>{item.section}: {formatMoneyText(item.message)}</strong>
          <small>{item.action}</small>
          {item.code.startsWith("source-reconciliation") && <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy)} type="button" onClick={reconcileSources}>{busy === "reconcile-sources" ? "Karşılaştırılıyor…" : "Kaynak mutabakatını çalıştır"}</button>}
        </div>)}
      </section>}

      </>}

      {!actionRequiredInCalculation && actionRequiredPanel}

      {!reviewing23 && <details className="panel smart-completion-panel smart-collapsible" id="smart-result" open={Boolean(workspace.latest_result) || calculationPending}>
        <summary className="panel-head">
          <div><p className="eyebrow">SONUÇ</p><h2>{provisionalResult?.title || (is439 && (current439Check || workspace.latest_result) ? "Hesaplama sonucu" : "KDV iadesini hesapla")}</h2></div>
          <span className="smart-collapse-meta"><em className={`pill smart-calculation-status ${calculationReady && !unresolvedKdv1SourceCount ? "active" : "partial"}`}>
            {backgroundActionCount > 0 && <span aria-hidden="true" className="smart-inline-progress"><i /></span>}
            {calculationStatus}
          </em><i aria-hidden="true">⌄</i></span>
        </summary>
        <div className="smart-collapsible-content">

        {calculationPending && <div role="status">Hesaplanıyor…</div>}
        {is439 && !calculationPending && workspace.latest_result?.status === "blocked" && <div className="issue warning" role="status">
          <strong>İade tutarı hesaplanamadı</strong>
          {calculationFailure ? <p>{formatMoneyText(calculationFailure)}</p> : <p>Bu hesap için açık kalan kontrolleri tamamlayın.</p>}
          {showActionRequired && <button type="button" className="secondary-button compact" onClick={() => scrollTo("smart-action-required")}>Açık kontrolleri göster</button>}
        </div>}
        {!is439 && !calculationPending && (calculationFailure || workspace.latest_result?.status === "blocked") && <div hidden className="issue warning" role="status">
          <strong>Hesaplama yapılamadı</strong>
          {calculationFailure && <p>{formatMoneyText(calculationFailure)}</p>}
          <ul>{resultIssues.map((item, index) => <li key={`${item.code}:${index}`}>
            {formatMoneyText(item.message)}
            {item.code === "missing-confirmed-recipes" && missingRecipeQuestions.length > 0 && <button type="button" className="secondary-button compact" disabled={Boolean(busy) || automaticPreparationBusy || anyUploadBusy}
              onClick={() => openPendingQuestion(missingRecipeQuestions[0])}>Reçete bağlantılarını göster</button>}
            {Array.isArray(item.metadata?.detail_messages) && <ul>{item.metadata.detail_messages.filter((detail): detail is string => typeof detail === "string").map((detail, detailIndex) => <li key={detailIndex}>{formatMoneyText(detail)}</li>)}</ul>}
          </li>)}</ul>
        </div>}

        {isAnnualReducedRate && !is439 && !sectorSaved && <div className="smart-sector-row">
          <span><strong>GİB sektör kodu</strong><small>Kapasite raporundaki ürün kodu GİB'in iade sektör kodu yerine geçmediği için yalnız bir kez girilir.</small></span>
          <label>Sektör kodu<input inputMode="numeric" pattern="[0-9]+" value={sectorCode} onChange={(event) => setSectorCode(event.target.value)} /></label>
          <label>Alt sektör (varsa)<input inputMode="numeric" pattern="[0-9]*" value={subsectorCode} onChange={(event) => setSubsectorCode(event.target.value)} /></label>
          <button className="secondary-button compact" disabled={!canPrepare || Boolean(busy) || anyUploadBusy || !sectorCode.trim()} type="button" onClick={saveSectorCode}>Kaydet</button>
        </div>}

        {workspace.latest_result?.status === "limited" && (provisionalResult || calculationBasisMissing) && <div className="issue warning">
          <strong>{provisionalResult?.title || (calculationBasisMissing ? "Hesaplama yapılamadı" : "Kapsamı sınırlı hesap")}</strong>
          {provisionalResult ? <p>Bu tutar doğrulanmış yüklenimlerle hesaplandı. {provisionalResult.unresolvedCount.toLocaleString("tr-TR")} alış satırındaki {money(provisionalResult.unresolvedVat)} kaynak KDV’nin tüketim veya satış/stok bağlantısı henüz tamamlanmadı; bu KDV geçici tutara dahil edilmedi. Bu kayıtlar açık kalıyor. <strong>Nihai iade tutarı, açık kayıtlar tamamlandıktan sonra belirlenir.</strong></p>
            : <p>{String(workspace.latest_result.summary.scope_message || (calculationBasisMissing ? "Hesabın dayanağını etkileyen kayıtlar henüz doğrulanamadı. Aşağıdaki açıklamalardan ilgili kayıtları inceleyebilirsiniz." : ""))}</p>}
          {Array.isArray(workspace.latest_result.summary.excluded_recipe_products) && (workspace.latest_result.summary.excluded_recipe_products as { id: number; code: string; name: string }[]).map(product => <p key={product.id}>{product.name} · {product.code} <button type="button" className="secondary-button compact" disabled={Boolean(busy) || automaticPreparationBusy || anyUploadBusy} onClick={() => void workflowAction("restore_recipe_product", { product_id: product.id })}>Kapsama geri al</button></p>)}
          {provisionalResult && Number(workspace.latest_result.summary.maximum_claimable_amount || 0) === 0 && <p><strong>Geçici tutarın sıfır olması, tamamlanmış dosyanın iade hakkının sıfır olduğu anlamına gelmez.</strong> Açık kayıtların yüklenimi henüz bu hesaba dahil değil.</p>}
          {!provisionalResult && !calculationBasisMissing && Number(workspace.latest_result.summary.maximum_claimable_amount || 0) === 0 && <p><strong>Bu sonuç, iade hakkınızın sıfır olduğu anlamına gelmez.</strong> Aşağıdaki tutarlar yalnız doğrulanabilen kapsamı gösterir. Hesaba alınmayan alış ve tüketimler bu sonuca dahil değildir.</p>}
          {workspace.latest_result.blockers?.length ? <details><summary>{provisionalResult ? "Geçici hesabı tamamlamak için bekleyen kayıtları göster" : "Hesabın dışında kalan belirsizlikleri göster"}</summary>{workspace.latest_result.blockers.map((item, index) => <div key={`${item.code}:${index}`}>
            <p>{formatMoneyText(item.message)}</p>
            {Array.isArray(item.metadata?.detail_messages) && <ul>{item.metadata.detail_messages.filter((message): message is string => typeof message === "string").map((message, detailIndex) => <li key={detailIndex}>{formatMoneyText(message)}</li>)}</ul>}
          </div>)}</details> : null}
        </div>}
        {workspace.latest_result && !calculationPending && !calculationFailure && calculatedAmount !== null && <div className="smart-calculation-result">
          <span><small>{is338 ? "İhracat bedeli" : "Doğrudan yüklenim"}</small><strong>{money(String(is338 ? workspace.latest_result.summary.delivery_amount : workspace.latest_result.summary.direct_vat || workspace.latest_result.summary.total_loaded_vat || "0"))}</strong></span>
          {is439 && <>
            <span><small>Genel giderlerden yüklenim</small><strong>{money(String(workspace.latest_result.summary.general_vat || "0"))}</strong></span>
            <span><small>Sabit kıymetlerden yüklenim</small><strong>{money(String(workspace.latest_result.summary.atik_vat || "0"))}</strong></span>
            <span><small>İndirimli satışlarda hesaplanan KDV</small><strong>{money(String(workspace.latest_result.summary.calculated_vat || "0"))}</strong></span>
          </>}
          <span><small>{is439 ? "Yüklenim üst sınırı" : "Azami iade"}</small><strong>{money(String(workspace.latest_result.summary.maximum_refundable_vat || "0"))}</strong></span>
          {isAnnualReducedRate ? <>
            <span><small>{provisionalResult?.amountLabel || (workspace.latest_result.status === "limited" ? "Doğrulanan kapsamla hesaplanan iade" : "Azami hesaplanan iade")}</small><strong>{money(calculatedAmount)}</strong></span>
          </> : <span><small>Hesaplanan iade</small><strong>{money(calculatedAmount)}</strong></span>}
          {is301 && <span><small>Normal usulde iade</small><strong>{money(String(workspace.latest_result.summary.standard_refundable_vat || "0"))}</strong></span>}
          {is301 && <span><small>İnceleme gerektiren tutar</small><strong>{money(String(workspace.latest_result.summary.inspection_required_vat || "0"))}</strong></span>}
        </div>}
        {is439 && workspace.latest_result?.status === "completed" && workspace.loading_integrity.excluded_count > 0 && <div className="issue warning">
          <strong>Bu hesap {workspace.loading_integrity.excluded_count.toLocaleString("tr-TR")} alış satırı dışarıda bırakılarak yapıldı.</strong>
          <p>Bu satırlardaki {money(workspace.loading_integrity.excluded_vat)} KDV hesaba dahil değil. Gösterilen sonuç yalnız dahil edilen kayıtlar içindir; dışarıda kalan KDV’nin tamamının iade alınabileceği anlamına gelmez.</p>
        </div>}
        {!provisionalResult && workspace.latest_result?.summary.assurance_required && <div className="issue warning">
          {String(workspace.latest_result.summary.assurance_message)}
        </div>}
        {!provisionalResult && requiresExportChain && workspace.latest_result?.status === "final" && <div className="smart-final-action-buttons">
          <a className="secondary-button compact" href={`/api/refund-cases/${refundCase.id}/export/input_vat`}>İndirilecek KDV</a>
          {is301 && <a className="secondary-button compact" href={`/api/refund-cases/${refundCase.id}/export/loaded_vat`}>Yüklenilen KDV</a>}
          <a className="secondary-button compact" href={`/api/refund-cases/${refundCase.id}/export/sales_invoice`}>İhracat satış</a>
          <a className="secondary-button compact" href={`/api/refund-cases/${refundCase.id}/export/customs_declaration`}>GÇB / ETGB</a>
          <a className="secondary-button compact" href={`/api/refund-cases/${refundCase.id}/export/refund_calculation`}>İade hesaplama</a>
        </div>}
        <div className="smart-final-action">
          <span>{backgroundActionCount > 0 && <span aria-hidden="true" className="smart-inline-progress"><i /></span>}{calculationInstruction}</span>
          <div className="smart-final-action-buttons">
            {is301 && <button className="secondary-button" disabled={!canPrepare || Boolean(busy) || anyUploadBusy || backgroundActionCount > 0} type="button" onClick={() => void calculate("draft")}>{busy === "calculate-draft" ? "Taslak hesaplanıyor…" : "Taslak hesapla"}</button>}
            {!is439 && <button
              className="primary-button"
              disabled={!canPrepare || Boolean(busy) || anyUploadBusy || automaticPreparationBusy || backgroundActionCount > 0 || !workspace.can_calculate || !sectorReady}
              type="button"
              onClick={() => void calculate("final")}
            >{busy === "calculate-final" ? "Kaydediliyor…" : automaticPreparationBusy ? "Hesap güncelleniyor…" : workspace.latest_result ? "İadeyi yeniden hesapla" : requiresExportChain ? "Nihai hesabı oluştur" : "İadeyi hesapla"}</button>}
          </div>
        </div>
        </div>
      </details>}
    </div>
  );
}
